import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { auth, db } from "../firebase";
import { API_BASE_URL } from "../config";
import Sidebar from "./Sidebar";
import IILSubnav from "./IILSubnav";
import { collection, doc, getDoc, getDocs, increment, onSnapshot, runTransaction, serverTimestamp, setDoc } from "firebase/firestore";
import { getActiveProfileInTransaction } from "../services/financeCore/clientTaxProfileService";
import { assertPeriodOpen } from "../services/financeCore/periodLockService";
import { buildSnapshotFields, snapshotDocRef } from "../services/financeCore/invoiceSnapshotService";
import { InvoiceOverrideRequiredError, buildCorrectionRecord, figureChanges, runWithCorrectionPrompt } from "../services/financeCore/invoiceCorrection";
import TaxTreatmentPanel from "./TaxTreatmentPanel";
import { advancePositions, planRecoveries } from "../services/iilAdvanceCalculator";
import { loadAdvanceLedger, postRecoveries, reverseRecoveries } from "../services/iilAdvanceService";

const FIXED_SERVICE_TOTAL_BY_SEGMENT = {
  "Pacakging of PPRC": 34706,
  "MECHINICAL MAINTAINANCE": 15590,
  "ADMIN SERVICE FACTORY III": 31907,
  "ADMIN Service Lahore Office": 11620,
  "ADMIN Service ISB Office": 2575,
  "Admin Multan": 2557,
  "IIL Trading Lahore": 1782,
  "IIL Trading ISB": 1782,
  "ISL Office Boy": 2575
};

const SERVICE_TOTAL_ALIASES = {
  "admin service factory 3": 31907,
  "admin service factory iii": 31907,
  "admin service factory": 31907,
  "maintenance staff": 15590,
  "mechinical maintainance": 15590,
  "mechanical maintenance": 15590,
  "admin service lahore office": 11620,
  "admin service isb office": 2575,
  "isl office boy": 2575,
  "isb office boy": 2575,
  "islamabad office boy": 2575,
  "admin multan": 2557,
  "amin multan": 2557,
  "iil trading lahore": 1782,
  "iil trading lhr": 1782,
  "iil tading isb": 1782,
  "iil trading isb": 1782,
  "pacakging of pprc": 34706,
  "packaging of pprc": 34706
};

const INVOICE_DESCRIPTIONS = {
  "Pacakging of PPRC": "Man Power Outsourcing Services for Packing of Polymer fitting at Its Polymer Division",
  "MECHINICAL MAINTAINANCE": "Man Power Outsourcing Services for Maintenance of Plant in Sheikhupura Factory.",
  "ADMIN SERVICE FACTORY III": "Man Power Outsourcing Services for Janitorial,Gardner and Painter at Sheikhupura Factory.",
  "ADMIN Service Lahore Office": "Man Power Outsourcing Services at IIL Lahore office Janitorial and office boy.",
  "ADMIN Service ISB Office": "Man Power Outsourcing Services for office boy at IIL Islamabad office",
  "Admin Multan": "Man Power Outsourcing Services for office boy at IIL Multan office",
  "IIL Trading Lahore": "Man Power Outsourcing Services for IIL Trading Lahore",
  "IIL Trading ISB": "Man Power Outsourcing Services for IIL Trading Islamabad",
  "ISL Office Boy": "Man Power Outsourcing Services for office boy at IIL Islamabad office"
};

const PACKER_TRANSPORT_TOTAL = 130000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const LAHORE_OFFICE_CODES = new Set(["10081", "10100", "10083", "10084", "10086", "10105", "10107"]);
const ISB_OFFICE_CODES = new Set(["10089", "10099"]);
const MULTAN_CODES = new Set(["10104", "10106"]);
const SOBIA_MAQSOOD_SALARY = 45000;
const HAMID_HUSSAIN_CODE = "10099";
const KASHIF_MULTAN_CODE = "10106";
const KASHIF_MULTAN_SALARY_BASE = 40000;
const KASHIF_MULTAN_SALARY_AMOUNT = 6000;
const CURRENT_DATE = new Date();
const IIL_FINANCIAL_YEAR_END = CURRENT_DATE.getMonth() >= 6 ? CURRENT_DATE.getFullYear() + 1 : CURRENT_DATE.getFullYear();
const IIL_FINANCIAL_YEAR_CODE = String(IIL_FINANCIAL_YEAR_END % 100).padStart(2, "0");
const IIL_COUNTER_ID = `IIL_${IIL_FINANCIAL_YEAR_CODE}`;
const IIL_INVOICE_PREFIX = `IIL-${IIL_FINANCIAL_YEAR_CODE}-`;
const IIL_INVOICE_START_NUMBER = 1001;
const IIL_PO_LIMITS = [
  { segment: "Pacakging of PPRC", allowedAmount: 997900, aliases: ["packaging of pprc"] },
  { segment: "Maintenance Staff", allowedAmount: 448350, aliases: ["mechinical maintainance", "mechanical maintenance"] },
  { segment: "Lahore Office Admin Staff", allowedAmount: 330300, aliases: ["admin service lahore office"] },
  { segment: "ISL-Office Boy", allowedAmount: 81000, aliases: ["isl office boy", "isb office boy", "admin service isb office"] },
  { segment: "Multan Office", allowedAmount: 99700, aliases: ["admin multan", "amin multan"] },
  { segment: "Admin Service Factory 3", allowedAmount: 914900, aliases: ["admin service factory iii", "admin service factory"] },
  { segment: "iil-Trading ISL", allowedAmount: 49874, aliases: ["iil trading isb", "iil trading isl", "iil tading isb"] },
  { segment: "iil-Trading LHR", allowedAmount: 99747, aliases: ["iil trading lahore", "iil trading lhr"] }
];
// Exact gross-up for a 7% client deduction on the final amount including 16% GST.
// Displayed commercially as 8.8376%; retaining the exact ratio prevents Rs. 1 rounding shortages.
const IIL_WHT_GROSSED_UP_RATE = (0.07 * 1.16) / (1 - (0.07 * 1.16));
const DEFAULT_IIL_CLIENT = {
  name: "International Industry LTD",
  address: "23-KM Sheikhupura Road Lahore",
  ntn: "0710735-8",
  strn: "02-04-7306-001-82"
};
const IIL_TRADING_CLIENT = {
  name: "IIL Trading PVT Limited",
  address: "Plot No. 91-C, 24th Commercial Street, Phase II Ext. Rd, DHA",
  ntn: "8282271-3",
  strn: "32-77-8762-347-76"
};
const TEMP_EMPLOYEE_REPLACEMENTS = {
  "10059": {
    sourceCode: "10059",
    code: "10109",
    name: "Sulman Masih"
  },
  "10109": {
    sourceCode: "10059",
    code: "10109",
    name: "Sulman Masih"
  }
};

function normalizeText(value) {
  return String(value ?? "").trim();
}

function normalizeHeader(value) {
  return normalizeText(value).toLowerCase().replace(/\s+/g, " ");
}

function matchesPoSegment(segmentName, poLimit) {
  const normalized = normalizeHeader(segmentName).replace(/-/g, " ");
  return [poLimit.segment, ...(poLimit.aliases || [])]
    .map((value) => normalizeHeader(value).replace(/-/g, " "))
    .includes(normalized);
}

function firestoreDate(value) {
  const date = value?.toDate?.() || (value ? new Date(value) : null);
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString("en-GB") : "";
}

function summaryDateValue(value) {
  const match = String(value || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return null;
  return new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
}

function poSummaryRemark(row) {
  if (row.difference == null) return "Not generated";
  if (row.difference > 0) return `Exceeds PO by PKR ${Math.round(row.difference).toLocaleString("en-PK")}`;
  if (row.difference < 0) return `Below PO by PKR ${Math.abs(Math.round(row.difference)).toLocaleString("en-PK")}`;
  return "Matches PO";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function clientDetailsForIILSegment(segmentName) {
  const normalized = normalizeHeader(segmentName);
  return normalized.includes("trading") || normalized.includes("tading")
    ? IIL_TRADING_CLIENT
    : DEFAULT_IIL_CLIENT;
}

function invoiceDescriptionForIILSegment(segmentName, period = {}) {
  const normalized = normalizeHeader(segmentName).replace(/-/g, " ");
  const from = period.from ? formatDate(period.from) : "";
  const to = period.to ? formatDate(period.to) : "";
  const periodSuffix = from && to ? ` (Period from ${from} to ${to})` : "";

  if (normalized.includes("trading") || normalized.includes("tading")) {
    const office = normalized.includes("lhr") || normalized.includes("lahore")
      ? "Lahore"
      : "Islamabad";
    return `Manpower Outsourcing Services for IIL Trading Office${office === "Lahore" ? " -" : ""} ${office}${periodSuffix}`;
  }

  return INVOICE_DESCRIPTIONS[segmentName] || `Man Power Outsourcing Services for ${segmentName}`;
}

function normalizeCode(value) {
  const text = normalizeText(value);
  if (!text) return "";
  const number = Number(text);
  return Number.isFinite(number) ? String(Math.trunc(number)) : text;
}

function toNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const number = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(number) ? number : fallback;
}

function parseOvertimeTotal(value) {
  const text = normalizeText(value);
  if (!text) return null;
  const normalized = text.toLowerCase().replace(/\s+/g, "");
  const colonMatch = normalized.match(/^(\d+(?:\.\d+)?):(\d+)/);
  if (colonMatch) return Number(colonMatch[1]) + Number(colonMatch[2]) / 60;
  const hourMinuteMatch = normalized.match(/^(\d+(?:\.\d+)?)(?:hrs?|hours?)(\d+(?:\.\d+)?)(?:mins?|minutes?)?/);
  if (hourMinuteMatch) return Number(hourMinuteMatch[1]) + Number(hourMinuteMatch[2]) / 60;
  const hourMatch = normalized.match(/^(\d+(?:\.\d+)?)(?:hrs?|hours?)?/);
  if (hourMatch && /[a-z]/.test(normalized)) return Number(hourMatch[1]);
  const number = Number(text.replace(/,/g, ""));
  return Number.isFinite(number) ? number : null;
}

function round(value, digits = 0) {
  const factor = 10 ** digits;
  return Math.round((toNumber(value) + Number.EPSILON) * factor) / factor;
}

function parseExcelDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === "number") {
    const date = new Date(Math.round((value - 25569) * MS_PER_DAY));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function daysInclusive(from, to) {
  if (!from || !to) return 0;
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.floor((end - start) / MS_PER_DAY) + 1;
}

function isRealPayrollRange(from, to) {
  const days = daysInclusive(from, to);
  return from && to && days >= 28 && days <= 31 &&
    from.getFullYear() >= 2000 && from.getFullYear() <= 2100 &&
    to.getFullYear() >= 2000 && to.getFullYear() <= 2100;
}

function monthNameToIndex(value) {
  return ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"]
    .indexOf(normalizeHeader(value).slice(0, 3));
}

function detectMonthYear(rows) {
  let monthIndex = -1;
  let year = null;

  rows.slice(0, 10).forEach((row) => {
    row.forEach((cell, index) => {
      const label = normalizeHeader(cell);
      if (label === "month") monthIndex = monthNameToIndex(row[index + 1]);
      if (label === "year") {
        const parsedYear = Number(row[index + 1]);
        if (Number.isFinite(parsedYear)) year = parsedYear;
      }
    });
  });

  if (monthIndex < 0 || !year) return null;
  return {
    from: new Date(year, monthIndex - 1, 21),
    to: new Date(year, monthIndex, 20)
  };
}

function formatDate(value) {
  const date = parseExcelDate(value);
  if (!date) return normalizeText(value);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function findHeaderRow(rows, requiredHeaders) {
  return rows.findIndex((row) => {
    const headers = row.map(normalizeHeader);
    return requiredHeaders.every((required) => headers.some((header) => header.includes(required)));
  });
}

function findColumn(headers, options) {
  return headers.findIndex((header) => options.some((option) => header.includes(option)));
}

function findEmployeeCodeColumn(headers) {
  return findColumn(headers, ["employee code", "emp id", "emp code", "employee id"]);
}

function compactHeader(value) {
  return normalizeHeader(value).replace(/[^a-z0-9]/g, "");
}

function normalizeEmployeeName(value) {
  return normalizeHeader(value)
    .replace(/\([^)]*\)/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function findTotalOvertimeColumn(headers) {
  return headers.findIndex((header) => {
    const normalized = normalizeHeader(header);
    const compact = compactHeader(header);
    const words = normalized.split(/[^a-z0-9]+/).filter(Boolean);
    const hasTotal = words.includes("total") || compact.includes("total");
    const hasOvertime = words.includes("overtime") || compact.includes("overtime");
    const hasOt = words.includes("ot") || compact.includes("totalot");

    return hasTotal && (hasOvertime || hasOt);
  });
}

function combineHeaderRows(primary = [], secondary = []) {
  const length = Math.max(primary.length, secondary.length);
  return Array.from({ length }, (_, index) => normalizeText(primary[index]) || normalizeText(secondary[index]));
}

function findOvertimeSheetRows(workbook, expectedCodes = new Set(), expectedNames = new Set()) {
  const candidates = workbook.SheetNames.flatMap((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    const sheetCandidates = [];

    rows.forEach((row, headerIndex) => {
      const combinedHeaders = combineHeaderRows(row, rows[headerIndex + 1]).map(normalizeHeader);
      const codeIndex = findEmployeeCodeColumn(combinedHeaders);
      const nameIndex = findColumn(combinedHeaders, ["name of employee", "employee name", "name"]);
      const totalIndex = findTotalOvertimeColumn(combinedHeaders);
      if (codeIndex < 0 || totalIndex < 0) return;

      const matchedCodes = rows.slice(headerIndex + 1).reduce((count, dataRow) => {
        const code = normalizeCode(dataRow[codeIndex]);
        return expectedCodes.has(code) ? count + 1 : count;
      }, 0);
      const matchedNames = rows.slice(headerIndex + 1).reduce((count, dataRow) => {
        const nameKey = nameIndex >= 0 ? normalizeEmployeeName(dataRow[nameIndex]) : "";
        return expectedNames.has(nameKey) ? count + 1 : count;
      }, 0);

      sheetCandidates.push({
        sheetName,
        rows,
        headerIndex,
        combinedHeaders,
        matchedCodes,
        matchedNames,
        preferred: normalizeHeader(sheetName).includes("overtime") ? 0 : 1
      });
    });

    return sheetCandidates;
  }).filter(Boolean);

  candidates.sort((a, b) => (
    b.matchedCodes - a.matchedCodes ||
    b.matchedNames - a.matchedNames ||
    a.preferred - b.preferred ||
    b.headerIndex - a.headerIndex
  ));
  return candidates[0] || null;
}

function segmentForEmployee(code, department) {
  const normalizedCode = normalizeCode(code);
  const normalizedDepartment = normalizeHeader(department);

  if (LAHORE_OFFICE_CODES.has(normalizedCode)) return "ADMIN Service Lahore Office";
  if (ISB_OFFICE_CODES.has(normalizedCode)) return "ADMIN Service ISB Office";
  if (MULTAN_CODES.has(normalizedCode)) return "Admin Multan";
  if (normalizedDepartment.includes("maintenance")) return "MECHINICAL MAINTAINANCE";
  if (normalizedDepartment.includes("production")) return "Pacakging of PPRC";
  if (normalizedDepartment.includes("jan")) return "ADMIN SERVICE FACTORY III";

  return "ADMIN SERVICE FACTORY III";
}

function parseProfileSheet(workbook) {
  const employees = [];

  workbook.SheetNames.forEach((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    const headerIndex = findHeaderRow(rows, ["employee code", "salary"]);
    if (headerIndex === -1) return;

    const headers = rows[headerIndex].map(normalizeHeader);
    const codeIndex = findEmployeeCodeColumn(headers);
    const cardIndex = findColumn(headers, ["employee card", "card no", "code#"]);
    const nameIndex = findColumn(headers, ["name of employee", "name"]);
    const fatherIndex = findColumn(headers, ["father"]);
    const cnicIndex = findColumn(headers, ["cnic"]);
    const bankIndex = findColumn(headers, ["bank name", "bank"]);
    const accountIndex = findColumn(headers, ["account number", "account no", "account #", "account"]);
    const pmtModeIndex = findColumn(headers, ["pmt mode", "payment mode", "mode"]);
    const departmentIndex = findColumn(headers, ["department"]);
    const segmentIndex = findColumn(headers, ["invoice segment", "client segment", "segment"]);
    const designationIndex = findColumn(headers, ["designation"]);
    const dojIndex = findColumn(headers, ["date of joining", "joining date", "joining", "doj"]);
    const salaryIndex = findColumn(headers, ["salary"]);

    rows.slice(headerIndex + 1).forEach((row) => {
      const code = normalizeCode(row[codeIndex]);
      const name = normalizeText(row[nameIndex]);
      const salary = toNumber(row[salaryIndex], NaN);
      if (!code || !name || !Number.isFinite(salary)) return;

      employees.push({
        code,
        cardNo: cardIndex >= 0 ? normalizeCode(row[cardIndex]) : "",
        name,
        fatherName: fatherIndex >= 0 ? normalizeText(row[fatherIndex]) : "",
        cnic: cnicIndex >= 0 ? normalizeText(row[cnicIndex]) : "",
        bank: bankIndex >= 0 ? normalizeText(row[bankIndex]) : "",
        account: accountIndex >= 0 ? normalizeText(row[accountIndex]) : "",
        pmtMode: pmtModeIndex >= 0 ? normalizeText(row[pmtModeIndex]) : "",
        department: departmentIndex >= 0 ? normalizeText(row[departmentIndex]) : "",
        designation: designationIndex >= 0 ? normalizeText(row[designationIndex]) : "",
        doj: dojIndex >= 0 ? row[dojIndex] : "",
        salary,
        segment: segmentIndex >= 0 && normalizeText(row[segmentIndex])
          ? normalizeText(row[segmentIndex])
          : segmentForEmployee(code, departmentIndex >= 0 ? row[departmentIndex] : "")
      });
    });
  });

  const byCode = new Map();
  employees.forEach((employee) => byCode.set(employee.code, employee));
  return byCode;
}

function buildMasterByName(masterByCode) {
  const byName = new Map();
  masterByCode.forEach((employee) => {
    const key = normalizeEmployeeName(employee.name);
    if (!key || byName.has(key)) return;
    byName.set(key, employee);
  });
  return byName;
}

function getSheetRows(workbook, preferredName) {
  const sheetName = workbook.SheetNames.find((name) => name.toLowerCase().includes(preferredName));
  if (!sheetName) return [];
  return XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
}

function isAttendanceHeader(value) {
  const header = normalizeHeader(value);
  if (!header) return false;
  if (/^(mon|tue|wed|thu|fri|sat|sun)/.test(header)) return true;
  const number = Number(header);
  return Number.isFinite(number) && number >= 1 && number <= 31;
}

function collectAttendanceColumns(rows, headerIndex, codeIndex, nameIndex) {
  const headers = rows[headerIndex] || [];
  const firstPossible = Math.max(codeIndex, nameIndex) + 1;
  const columns = [];

  for (let col = firstPossible; col < headers.length; col += 1) {
    const header = normalizeHeader(headers[col]);
    if (header.includes("total") || ["p", "a", "s", "g"].includes(header) || header.includes("salary days")) break;
    if (isAttendanceHeader(headers[col])) columns.push(col);
  }

  return columns;
}

function countDeductions(marks) {
  const normalized = marks.map((mark) => normalizeText(mark).toUpperCase());
  let deductions = 0;

  normalized.forEach((mark) => {
    if (mark === "A") deductions += 1;
    if (mark === "H") deductions += 0.5;
  });

  let index = 0;
  while (index < normalized.length) {
    if (!["R", "S", "GH"].includes(normalized[index])) {
      index += 1;
      continue;
    }

    const blockStart = index;
    while (index < normalized.length && ["R", "S", "GH"].includes(normalized[index])) index += 1;

    const before = normalized[blockStart - 1];
    const after = normalized[index];
    if (before === "A" && after === "A") deductions += index - blockStart;
  }

  return deductions;
}

function findAttendanceHeaderIndexes(rows) {
  return rows.reduce((indexes, row, index) => {
    const headers = row.map(normalizeHeader);
    const nameIndex = findColumn(headers, ["name of employee", "employee name", "employee name", "name"]);
    const codeIndex = findEmployeeCodeColumn(headers);
    const attendanceColumns = collectAttendanceColumns(rows, index, codeIndex, nameIndex);
    if (nameIndex >= 0 && attendanceColumns.length) indexes.push(index);
    return indexes;
  }, []);
}

function parseAttendanceBlock(rows, headerIndex, sheetName) {
  const headers = rows[headerIndex].map(normalizeHeader);
  const codeIndex = findEmployeeCodeColumn(headers);
  const nameIndex = findColumn(headers, ["name of employee", "employee name", "name"]);
  const attendanceColumns = collectAttendanceColumns(rows, headerIndex, codeIndex, nameIndex);
  const salaryDaysIndex = findColumn(headers, ["salary days", "salary day"]);
  const fallbackPeriodDays = attendanceColumns.length || 30;
  const parsedRows = [];

  for (let rowIndex = headerIndex + 1; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    const normalizedRow = row.map(normalizeHeader);
    const nextNameIndex = findColumn(normalizedRow, ["name of employee", "employee name", "name"]);
    const hasNextAttendanceColumns = collectAttendanceColumns(rows, rowIndex, findEmployeeCodeColumn(normalizedRow), nextNameIndex).length > 0;
    const firstText = normalizeHeader(row.find((cell) => normalizeText(cell)));

    if (firstText.includes("attendance") || firstText.includes("attandence") || (nextNameIndex >= 0 && hasNextAttendanceColumns)) break;

    const code = codeIndex >= 0 ? normalizeCode(row[codeIndex]) : "";
    const uploadedName = nameIndex >= 0 ? normalizeText(row[nameIndex]) : "";
    if (!code && !uploadedName) continue;

    const marks = attendanceColumns.map((col) => row[col]);
    const calculatedDeductions = countDeductions(marks);
    const explicitSalaryDays = salaryDaysIndex >= 0 ? toNumber(row[salaryDaysIndex], NaN) : NaN;
    const daysWorked = Number.isFinite(explicitSalaryDays)
      ? Math.max(Math.min(explicitSalaryDays, fallbackPeriodDays), 0)
      : Math.max(fallbackPeriodDays - calculatedDeductions, 0);
    const deductions = Number.isFinite(explicitSalaryDays)
      ? Math.max(fallbackPeriodDays - daysWorked, 0)
      : calculatedDeductions;

    parsedRows.push({
      code,
      uploadedName,
      nameKey: normalizeEmployeeName(uploadedName),
      sourceSheet: sheetName,
      deductions,
      daysWorked,
      periodDays: fallbackPeriodDays
    });
  }

  return parsedRows;
}

function parseAttendanceRows(workbook) {
  const attendanceRows = [];

  workbook.SheetNames.forEach((sheetName) => {
    if (normalizeHeader(sheetName).includes("overtime")) return;
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    const headerIndexes = findAttendanceHeaderIndexes(rows);
    const latestHeaderIndex = headerIndexes[headerIndexes.length - 1];
    if (latestHeaderIndex == null) return;
    attendanceRows.push(...parseAttendanceBlock(rows, latestHeaderIndex, sheetName));
  });

  if (!attendanceRows.length) {
    throw new Error("Attendance workbook must contain at least one sheet with Employee Name and attendance date columns.");
  }

  return attendanceRows;
}

function parseOvertimeByCode(workbook, attendanceRows = []) {
  const expectedCodes = new Set();
  const expectedNames = new Set();
  attendanceRows.forEach((row) => {
    if (row.code) expectedCodes.add(row.code);
    if (row.nameKey) expectedNames.add(row.nameKey);
    const replacement = TEMP_EMPLOYEE_REPLACEMENTS[row.code];
    if (replacement?.code) expectedCodes.add(replacement.code);
    if (replacement?.sourceCode) expectedCodes.add(replacement.sourceCode);
  });
  const overtimeSheet = findOvertimeSheetRows(workbook, expectedCodes, expectedNames);
  if (!overtimeSheet) {
    const hasOvertimeSheet = workbook.SheetNames.some((sheetName) => normalizeHeader(sheetName).includes("overtime"));
    if (!hasOvertimeSheet) return new Map();
    throw new Error("Overtime sheet must contain a Total Overtime column.");
  }

  const { rows, headerIndex, combinedHeaders } = overtimeSheet;
  const headers = combinedHeaders || rows[headerIndex].map(normalizeHeader);
  const codeIndex = findEmployeeCodeColumn(headers);
  const nameIndex = findColumn(headers, ["name of employee", "employee name", "name"]);
  const totalIndex = findTotalOvertimeColumn(headers);
  const overtimeByCode = new Map();

  rows.slice(headerIndex + 1).forEach((row) => {
    const code = normalizeCode(row[codeIndex]);
    const nameKey = nameIndex >= 0 ? normalizeEmployeeName(row[nameIndex]) : "";
    const total = parseOvertimeTotal(row[totalIndex]);
    if (total == null) return;
    if (code) overtimeByCode.set(code, total);
    if (nameKey) overtimeByCode.set(nameKey, total);
  });

  return overtimeByCode;
}

function periodFromAttendanceWorkbook(workbook, attendanceRows) {
  const rows = getSheetRows(workbook, "attendance");
  const explicitFrom = parseExcelDate(rows?.[5]?.[0]);
  const explicitTo = parseExcelDate(rows?.[5]?.[1]);

  if (isRealPayrollRange(explicitFrom, explicitTo)) {
    return { from: explicitFrom, to: explicitTo, days: daysInclusive(explicitFrom, explicitTo) };
  }

  const detected = detectMonthYear(rows);
  if (detected) {
    return { ...detected, days: attendanceRows[0]?.periodDays || daysInclusive(detected.from, detected.to) };
  }

  const days = attendanceRows[0]?.periodDays || 30;
  return { from: "", to: "", days };
}

function periodText(period) {
  if (period.from && period.to) return `${formatDate(period.from)} - ${formatDate(period.to)}`;
  return `${period.days || 30} day payroll period`;
}

function parseDateInput(value) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseTrailingNumber(value) {
  const match = String(value || "").match(/(\d+)$/);
  return match ? Number(match[1]) : 0;
}

function formatIILInvoiceNumber(number) {
  return `${IIL_INVOICE_PREFIX}${String(Number(number || 0)).padStart(4, "0")}`;
}

function fixedServiceTotalForSegment(segmentName) {
  const exact = FIXED_SERVICE_TOTAL_BY_SEGMENT[segmentName];
  if (exact != null) return exact;
  const normalized = normalizeHeader(segmentName);
  if (SERVICE_TOTAL_ALIASES[normalized] != null) return SERVICE_TOTAL_ALIASES[normalized];
  if (normalized.includes("factory") && normalized.includes("3")) return 31907;
  if (normalized.includes("maintenance")) return 15590;
  if (normalized.includes("trading") || normalized.includes("tading")) return 1782;
  if (normalized.includes("lahore")) return 11620;
  if (normalized.includes("isb") || normalized.includes("isl") || normalized.includes("islamabad")) return 2575;
  if (normalized.includes("multan")) return 2557;
  if (normalized.includes("pprc") || normalized.includes("packing") || normalized.includes("packaging")) return 34706;
  return 0;
}

function lookupCodeValue(value) {
  const text = normalizeCode(value);
  return /^\d+$/.test(text) ? Number(text) : text;
}

function salaryForIIL(row) {
  if (normalizeEmployeeName(row.name) === "sobia maqsood") return SOBIA_MAQSOOD_SALARY;
  if (normalizeCode(row.code) === KASHIF_MULTAN_CODE) return KASHIF_MULTAN_SALARY_BASE;
  return toNumber(row.salary);
}

function billableDaysForIIL(row, salary, periodDays) {
  const code = normalizeCode(row.code);
  const deductions = toNumber(row.deductions);

  if (code === HAMID_HUSSAIN_CODE) {
    return Math.max(periodDays / 2 - deductions, 0);
  }

  if (code === KASHIF_MULTAN_CODE) {
    const perDay = periodDays > 0 ? salary / periodDays : 0;
    return perDay > 0 ? KASHIF_MULTAN_SALARY_AMOUNT / perDay : 0;
  }

  return toNumber(row.daysWorked);
}

function processSegment(segmentName, rows, periodDays) {
  const fixedServiceTotal = fixedServiceTotalForSegment(segmentName);
  const employeeCount = rows.length || 1;
  const isPacker = segmentName.toLowerCase().includes("pprc");
  const serviceBase = Math.floor(fixedServiceTotal / employeeCount);
  const serviceRemainder = fixedServiceTotal - serviceBase * employeeCount;
  const transportBase = isPacker ? Math.floor(PACKER_TRANSPORT_TOTAL / employeeCount) : 0;
  const transportRemainder = isPacker ? PACKER_TRANSPORT_TOTAL - transportBase * employeeCount : 0;

  const processedRows = rows.map((row, rowIndex) => {
    const salary = salaryForIIL(row);
    const daysWorked = billableDaysForIIL(row, salary, periodDays);
    const otHours = toNumber(row.otHours);
    const perDay = periodDays > 0 ? salary / periodDays : 0;
    const salaryAmount = perDay * daysWorked;
    const otAmount = (otHours / 8) * perDay;
    const pessi = Math.min(salaryAmount, 44400) * 0.06;
    const eobi = Math.min(salaryAmount, 40000) * 0.05;
    const educationCess = 8.33;
    const groupLifeInsurance = 148;
    const transport = transportBase + (rowIndex < transportRemainder ? 1 : 0);
    const totalCost = salaryAmount + otAmount + pessi + eobi + educationCess + groupLifeInsurance + transport;
    const service = serviceBase + (rowIndex < serviceRemainder ? 1 : 0);
    const wht = (totalCost + service) * IIL_WHT_GROSSED_UP_RATE;
    const invoiceAmount = totalCost + service + wht;
    const gst = invoiceAmount * 0.16;
    const grandTotal = invoiceAmount + gst;

    return {
      ...row,
      "Employee Code": lookupCodeValue(row.code),
      "Card No": lookupCodeValue(row.cardNo),
      Name: row.name,
      CNIC: row.cnic || "",
      Bank: row.bank || "",
      Account: row.account || "",
      "PMT Mode": row.pmtMode || "",
      Segment: segmentName,
      DOJ: row.doj ? formatDate(row.doj) : "",
      Salary: Math.round(salary),
      "Month Days": periodDays,
      "Days Worked": daysWorked,
      Deductions: row.deductions,
      "P/D Rate": round(perDay),
      "Salary Amount": round(salaryAmount),
      "OT Hours": otHours,
      "OT Amount": round(otAmount),
      PESSI: round(pessi),
      EOBI: round(eobi),
      "Education Cess": round(educationCess),
      "Group Life Insurance": groupLifeInsurance,
      ...(isPacker ? { "Transport Cost": round(transport) } : {}),
      "Total Cost": round(totalCost),
      "Service Charges": round(service),
      "With Holding Tax": round(wht),
      "Invoice Amount": round(invoiceAmount),
      "GST Tax 16%": round(gst),
      "Total Invoice Value": round(grandTotal)
    };
  });

  // Invoice WHT is recovered at segment level because the client deducts 7% from
  // the final gross invoice, not independently from each employee line.
  const segmentBase = processedRows.reduce((sum, row) =>
    sum + toNumber(row["Total Cost"]) + toNumber(row["Service Charges"]), 0);
  const targetWht = round(segmentBase * IIL_WHT_GROSSED_UP_RATE);
  const currentWht = processedRows.reduce((sum, row) => sum + toNumber(row["With Holding Tax"]), 0);
  if (processedRows.length) {
    const lastRow = processedRows[processedRows.length - 1];
    lastRow["With Holding Tax"] = toNumber(lastRow["With Holding Tax"]) + targetWht - currentWht;
    lastRow["Invoice Amount"] = toNumber(lastRow["Total Cost"])
      + toNumber(lastRow["Service Charges"])
      + toNumber(lastRow["With Holding Tax"]);
  }

  const targetGst = round(processedRows.reduce((sum, row) => sum + toNumber(row["Invoice Amount"]), 0) * 0.16);
  const currentGst = processedRows.reduce((sum, row) => sum + round(toNumber(row["Invoice Amount"]) * 0.16), 0);
  processedRows.forEach((row) => {
    row["GST Tax 16%"] = round(toNumber(row["Invoice Amount"]) * 0.16);
    row["Total Invoice Value"] = toNumber(row["Invoice Amount"]) + toNumber(row["GST Tax 16%"]);
  });
  if (processedRows.length) {
    const lastRow = processedRows[processedRows.length - 1];
    lastRow["GST Tax 16%"] += targetGst - currentGst;
    lastRow["Total Invoice Value"] = toNumber(lastRow["Invoice Amount"]) + toNumber(lastRow["GST Tax 16%"]);
  }

  return processedRows;
}

function summarizeRows(rows) {
  const summary = rows.reduce((sum, row) => ({
    salaryAmount: sum.salaryAmount + toNumber(row["Salary Amount"]),
    otAmount: sum.otAmount + toNumber(row["OT Amount"]),
    pessi: sum.pessi + toNumber(row.PESSI),
    eobi: sum.eobi + toNumber(row.EOBI),
    totalCost: sum.totalCost + toNumber(row["Total Cost"]),
    serviceCharges: sum.serviceCharges + toNumber(row["Service Charges"]),
    wht: sum.wht + toNumber(row["With Holding Tax"]),
    invoiceAmount: sum.invoiceAmount + toNumber(row["Invoice Amount"]),
    gst: sum.gst + toNumber(row["GST Tax 16%"]),
    totalInvoiceValue: sum.totalInvoiceValue + toNumber(row["Total Invoice Value"])
  }), {
    salaryAmount: 0,
    otAmount: 0,
    pessi: 0,
    eobi: 0,
    totalCost: 0,
    serviceCharges: 0,
    wht: 0,
    invoiceAmount: 0,
    gst: 0,
    totalInvoiceValue: 0
  });
  const invoiceAmount = round(summary.invoiceAmount);
  const gst = round(invoiceAmount * 0.16);
  return {
    ...summary,
    invoiceAmount,
    gst,
    totalInvoiceValue: invoiceAmount + gst
  };
}

function parseAdvanceSheet(workbook) {
  const advanceByCode = new Map();

  workbook.SheetNames.forEach((sheetName) => {
    const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    const headerIndex = rows.findIndex((row) => {
      const headers = row.map(normalizeHeader);
      return findEmployeeCodeColumn(headers) >= 0 && findColumn(headers, ["advance", "other deduction", "deduction amount"]) >= 0;
    });
    if (headerIndex < 0) return;

    const headers = rows[headerIndex].map(normalizeHeader);
    const codeIndex = findEmployeeCodeColumn(headers);
    const advanceIndex = findColumn(headers, ["advance", "other deduction", "deduction amount"]);

    rows.slice(headerIndex + 1).forEach((row) => {
      const code = normalizeCode(row[codeIndex]);
      const amount = round(row[advanceIndex]);
      if (!code || !amount) return;
      advanceByCode.set(code, (advanceByCode.get(code) || 0) + amount);
    });
  });

  return advanceByCode;
}

function buildIILPayrollRows(segments, getInvoiceNumber, advanceByCode = new Map(), recordedPlan = null) {
  return segments.flatMap((segment, segmentIndex) => {
    const invoiceNumber = getInvoiceNumber(segmentIndex);
    return segment.rows.map((row) => {
      const employeeCode = row["Employee Code"] || row.code || "";
      const sheetAdvance = round(advanceByCode.get(normalizeCode(employeeCode)) || 0);
      const recorded = recordedPlan?.byCode.get(normalizeCode(employeeCode));
      const recordedAdvance = recorded ? recorded.amount : 0;
      const advance = round(sheetAdvance + recordedAdvance);
      const eobiDeduction = 400;
      const salaryAmount = round(row["Salary Amount"]);
      const otAmount = round(row["OT Amount"]);
      const grossSalary = round(salaryAmount + otAmount);
      const totalDeduction = round(eobiDeduction + advance);
      const netPay = round(grossSalary - totalDeduction);
      const payableDifference = round(grossSalary - netPay);

      return {
        "EMPLOYEE ID": employeeCode,
        "EMPLOYEE NAME": row.Name || row.name || "",
        "Net Pay": netPay,
        "Account #": row.Account || "",
        Bank: row.Bank || "",
        "PMT Mode": row["PMT Mode"] || "",
        CNIC: row.CNIC || "",
        Segment: segment.name,
        "Invoice Number": invoiceNumber,
        "Salary Amount": salaryAmount,
        "OT Amount": otAmount,
        "Gross Salary": grossSalary,
        "Salary and OT Charged to Client": grossSalary,
        "EOBI Deduction": eobiDeduction,
        Advance: advance,
        "Advance Recovered (Recorded)": recordedAdvance,
        "Advance Balance After": recorded ? recorded.balanceAfter : "",
        "Total Deduction": totalDeduction,
        "Actual Wages Paid": netPay,
        "Client vs Payable Difference": payableDifference,
        "Comparison Status": payableDifference === 0 ? "Matched" : "Different",
        "Days Worked": row["Days Worked"] || "",
        Deductions: row.Deductions || 0
      };
    });
  });
}

function payrollSegmentsFromEntries(entries = []) {
  const grouped = new Map();
  entries.forEach((entry) => {
    if (!grouped.has(entry.segmentName)) grouped.set(entry.segmentName, []);
    grouped.get(entry.segmentName).push(entry.row);
  });
  return [...grouped.entries()].map(([name, rows]) => ({ name, rows, summary: summarizeRows(rows) }));
}

function summarizePayrollRows(rows) {
  return rows.reduce((summary, row) => {
    const hasAccount = normalizeText(row["Account #"]);
    return {
      employeeCount: summary.employeeCount + 1,
      totalNetPay: summary.totalNetPay + toNumber(row["Net Pay"]),
      totalGrossSalary: summary.totalGrossSalary + toNumber(row["Gross Salary"]),
      totalChargedToClient: summary.totalChargedToClient + toNumber(row["Salary and OT Charged to Client"]),
      totalActualWagesPaid: summary.totalActualWagesPaid + toNumber(row["Actual Wages Paid"]),
      totalPayableDifference: summary.totalPayableDifference + toNumber(row["Client vs Payable Difference"]),
      totalDeductions: summary.totalDeductions + toNumber(row["Total Deduction"]),
      differenceRows: summary.differenceRows + (toNumber(row["Client vs Payable Difference"]) !== 0 ? 1 : 0),
      bankRows: summary.bankRows + (hasAccount ? 1 : 0),
      chequeRows: summary.chequeRows + (hasAccount ? 0 : 1)
    };
  }, {
    employeeCount: 0,
    totalNetPay: 0,
    totalGrossSalary: 0,
    totalChargedToClient: 0,
    totalActualWagesPaid: 0,
    totalPayableDifference: 0,
    totalDeductions: 0,
    differenceRows: 0,
    bankRows: 0,
    chequeRows: 0
  });
}

function buildIILPayrollWorkbook(rows, segments, period, completedAt = "") {
  const payrollHeaders = [
    "EMPLOYEE ID",
    "EMPLOYEE NAME",
    "Net Pay",
    "Account #",
    "Bank",
    "PMT Mode",
    "CNIC",
    "Segment",
    "Invoice Number",
    "Salary Amount",
    "OT Amount",
    "Gross Salary",
    "Salary and OT Charged to Client",
    "EOBI Deduction",
    "Advance",
    "Advance Recovered (Recorded)",
    "Advance Balance After",
    "Total Deduction",
    "Actual Wages Paid",
    "Client vs Payable Difference",
    "Comparison Status",
    "Days Worked",
    "Deductions"
  ];
  const summary = summarizePayrollRows(rows);
  const payrollData = [
    ["ALM OUTSOURCING SERVICES PVT LTD"],
    ["IIL PAYROLL SHEET"],
    [`Period: ${periodText(period)}`],
    completedAt ? [`Completed: ${completedAt}`] : ["Status: Draft"],
    [],
    payrollHeaders,
    ...rows.map((row) => payrollHeaders.map((header) => row[header] ?? ""))
  ];
  const totalRow = payrollHeaders.map((header, index) => {
    if (index === 0) return "TOTAL";
    if (["Net Pay", "Salary Amount", "OT Amount", "Gross Salary", "Salary and OT Charged to Client", "EOBI Deduction", "Advance", "Advance Recovered (Recorded)", "Total Deduction", "Actual Wages Paid", "Client vs Payable Difference", "Days Worked", "Deductions"].includes(header)) {
      const total = rows.reduce((sum, row) => sum + toNumber(row[header]), 0);
      return total ? round(total) : "";
    }
    return "";
  });
  payrollData.push(totalRow);

  const summaryData = [
    ["Metric", "Value"],
    ["Payroll Period", periodText(period)],
    ["Completed At", completedAt || "Draft"],
    ["Employees", summary.employeeCount],
    ["Gross Salary", round(summary.totalGrossSalary)],
    ["Salary and OT Charged to Client", round(summary.totalChargedToClient)],
    ["Actual Wages Paid", round(summary.totalActualWagesPaid)],
    ["Client vs Payable Difference", round(summary.totalPayableDifference)],
    ["Different Employee Rows", summary.differenceRows],
    ["Total Deductions", round(summary.totalDeductions)],
    ["Total Net Pay", round(summary.totalNetPay)],
    ["Bank Transfer Rows", summary.bankRows],
    ["Cheque Rows", summary.chequeRows],
    [],
    ["Invoice Number", "Segment", "Employees", "Salary Amount", "OT Amount", "Charged to Client", "Actual Wages Paid", "Difference"]
  ];
  segments.forEach((segment, index) => {
    const segmentRows = rows.filter((row) => row.Segment === segment.name);
    summaryData.push([
      segmentRows[0]?.["Invoice Number"] || "",
      segment.name,
      segmentRows.length,
      round(segmentRows.reduce((sum, row) => sum + toNumber(row["Salary Amount"]), 0)),
      round(segmentRows.reduce((sum, row) => sum + toNumber(row["OT Amount"]), 0)),
      round(segmentRows.reduce((sum, row) => sum + toNumber(row["Salary and OT Charged to Client"]), 0)),
      round(segmentRows.reduce((sum, row) => sum + toNumber(row["Actual Wages Paid"]), 0)),
      round(segmentRows.reduce((sum, row) => sum + toNumber(row["Client vs Payable Difference"]), 0))
    ]);
  });

  const wb = XLSX.utils.book_new();
  const payrollSheet = XLSX.utils.aoa_to_sheet(payrollData);
  payrollSheet["!cols"] = payrollHeaders.map((header) => ({ wch: Math.max(14, Math.min(28, header.length + 4)) }));
  payrollSheet["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: payrollHeaders.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: payrollHeaders.length - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: payrollHeaders.length - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: payrollHeaders.length - 1 } }
  ];
  payrollSheet["!autofilter"] = {
    ref: XLSX.utils.encode_range({ s: { r: 5, c: 0 }, e: { r: payrollData.length - 1, c: payrollHeaders.length - 1 } })
  };

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  summarySheet["!cols"] = [{ wch: 24 }, { wch: 36 }, { wch: 14 }, { wch: 18 }, { wch: 16 }, { wch: 18 }];

  XLSX.utils.book_append_sheet(wb, payrollSheet, "Payroll Upload");
  XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");
  return XLSX.write(wb, { bookType: "xlsx", type: "array" });
}

function cleanFileName(value) {
  return String(value || "Invoice").replace(/[\\/:*?"<>|]/g, "_").replace(/\s+/g, "_");
}

function buildIILInvoiceSheet(segment, period, invoiceNumber) {
  const summary = segment.summary || {};
  const client = clientDetailsForIILSegment(segment.name);
  const description = invoiceDescriptionForIILSegment(segment.name, period);
  const dateFrom = period.from ? formatDate(period.from) : "";
  const dateTo = period.to ? formatDate(period.to) : "";

  const rows = [
    ["Punjab Sales Tax Invoice"],
    [],
    ["Date", new Date().toLocaleDateString("en-GB"), "", "Invoice #", invoiceNumber],
    ["ALM NTN #", "3066078-5", "", "ALM PSTN #", "P3066078-5"],
    [],
    ["Customer Name", client.name, "", "Invoice Number", invoiceNumber],
    ["Address", client.address, "", "N.T.N No.", client.ntn],
    ["", "", "", "STRN", client.strn],
    ["Date From", dateFrom, "", "To", dateTo],
    ["Segment", segment.name],
    [],
    ["Date From", "To", "Description", "Invoice Amount", "Sales Tax", "Total Amount"],
    [
      dateFrom,
      dateTo,
      description,
      round(summary.invoiceAmount),
      round(summary.gst),
      round(summary.totalInvoiceValue)
    ],
    [],
    ["", "", "", "", "Grand Total / Net Payable", round(summary.totalInvoiceValue)]
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = [
    { wch: 18 },
    { wch: 24 },
    { wch: 58 },
    { wch: 18 },
    { wch: 24 },
    { wch: 18 }
  ];
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 9, c: 1 }, e: { r: 9, c: 5 } }
  ];

  return ws;
}

function xmlEscape(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function styleCellXml(cellXml, styleId) {
  if (/\ss="/.test(cellXml)) return cellXml.replace(/\ss="[^"]*"/, ` s="${styleId}"`);
  return cellXml.replace("<c ", `<c s="${styleId}" `);
}

function buildIILStylesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <numFmts count="1"><numFmt numFmtId="164" formatCode="#,##0"/></numFmts>
  <fonts count="3">
    <font><sz val="12"/><name val="Calibri"/></font>
    <font><b/><sz val="13"/><color rgb="FF0B3D6D"/><name val="Calibri"/></font>
    <font><b/><sz val="12"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>
  </fonts>
  <fills count="5">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF0B3D6D"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF7FAFC"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFEAF2FB"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="2">
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FFB8C2CC"/></left>
      <right style="thin"><color rgb="FFB8C2CC"/></right>
      <top style="thin"><color rgb="FFB8C2CC"/></top>
      <bottom style="thin"><color rgb="FFB8C2CC"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="8">
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="3" borderId="1" xfId="0" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="1" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
    <xf numFmtId="164" fontId="1" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyFont="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
  <dxfs count="0"/>
  <tableStyles count="1" defaultTableStyle="TableStyleMedium2" defaultPivotStyle="PivotStyleMedium9"/>
</styleSheet>`;
}

function applyIILSheetXmlStyles(sheetXml, rowCount) {
  const styledCells = sheetXml.replace(/<c\b[^>]*\br="([A-Z]+)(\d+)"[^>]*>[\s\S]*?<\/c>/g, (cellXml, colLetters, rowText) => {
    const rowNumber = Number(rowText);
    const colIndex = XLSX.utils.decode_col(colLetters);
    const isAmountColumn = colIndex >= 5;

    if (rowNumber >= 2 && rowNumber <= 4) return styleCellXml(cellXml, 1);
    if (rowNumber === 6) return styleCellXml(cellXml, 2);
    if (rowNumber === rowCount) return styleCellXml(cellXml, isAmountColumn ? 7 : 5);
    if (rowNumber > 6 && rowNumber < rowCount) {
      return styleCellXml(cellXml, isAmountColumn ? 6 : 3);
    }

    return cellXml;
  });

  let xml = styledCells;
  if (!xml.includes("xmlns:r=")) {
    xml = xml.replace("<worksheet ", '<worksheet xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ');
  }
  xml = xml.replace(/<sheetViews>[\s\S]*?<\/sheetViews>/, "");
  xml = xml.replace(/<autoFilter[^>]*\/>/, "");
  xml = xml.replace(/<sheetFormatPr\b([^>]*)>/, '<sheetViews><sheetView workbookViewId="0"><pane ySplit="6" topLeftCell="A7" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft"/></sheetView></sheetViews><sheetFormatPr$1>');
  xml = xml.replace(/<tableParts[\s\S]*?<\/tableParts>/, "");
  return xml.replace("</worksheet>", '<tableParts count="1"><tablePart r:id="rIdIILTable"/></tableParts></worksheet>');
}

function applyIILInvoiceSheetXmlStyles(sheetXml) {
  const styledCells = sheetXml.replace(/<c\b[^>]*\br="([A-Z]+)(\d+)"[^>]*>[\s\S]*?<\/c>/g, (cellXml, colLetters, rowText) => {
    const rowNumber = Number(rowText);
    const colIndex = XLSX.utils.decode_col(colLetters);
    const isAmountColumn = colIndex >= 3;

    if (rowNumber === 1) return styleCellXml(cellXml, 1);
    if (rowNumber === 11) return styleCellXml(cellXml, 2);
    if (rowNumber === 12) return styleCellXml(cellXml, isAmountColumn ? 6 : 3);
    if (rowNumber === 14) return styleCellXml(cellXml, colIndex === 5 ? 7 : 5);
    if ([3, 4, 6, 7, 8, 9, 10].includes(rowNumber)) {
      return styleCellXml(cellXml, [0, 3].includes(colIndex) ? 5 : 3);
    }

    return cellXml;
  });

  let xml = styledCells;
  xml = xml.replace(/<sheetViews>[\s\S]*?<\/sheetViews>/, "");
  xml = xml.replace(/<sheetFormatPr\b([^>]*)>/, '<sheetViews><sheetView workbookViewId="0" zoomScale="90" zoomScaleNormal="90"/></sheetViews><sheetFormatPr$1>');
  xml = xml.replace(/<row r="1"[^>]*>/, '<row r="1" ht="28" customHeight="1">');
  xml = xml.replace(/<row r="12"[^>]*>/, '<row r="12" ht="42" customHeight="1">');
  return xml;
}

async function styleIILWorkbookBuffer(buffer, data, keys) {
  const zip = await JSZip.loadAsync(buffer);
  const rowCount = data.length;
  const lastCol = XLSX.utils.encode_col(keys.length - 1);
  const tableRef = `A6:${lastCol}${rowCount}`;
  const sheetPath = "xl/worksheets/sheet1.xml";
  const relsPath = "xl/worksheets/_rels/sheet1.xml.rels";
  const contentTypesPath = "[Content_Types].xml";

  const sheetXml = await zip.file(sheetPath).async("string");
  const invoiceSheetPath = "xl/worksheets/sheet2.xml";
  const invoiceSheet = zip.file(invoiceSheetPath);
  const tableColumns = keys.map((key, index) => `<tableColumn id="${index + 1}" name="${xmlEscape(key)}"/>`).join("");
  const tableXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<table xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" id="1" name="IILInvoiceTable" displayName="IILInvoiceTable" ref="${tableRef}" totalsRowShown="0">
  <autoFilter ref="${tableRef}"/>
  <tableColumns count="${keys.length}">${tableColumns}</tableColumns>
  <tableStyleInfo name="TableStyleMedium2" showFirstColumn="0" showLastColumn="0" showRowStripes="1" showColumnStripes="0"/>
</table>`;
  const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rIdIILTable" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/table" Target="../tables/table1.xml"/>
</Relationships>`;
  let contentTypesXml = await zip.file(contentTypesPath).async("string");
  if (!contentTypesXml.includes("/xl/tables/table1.xml")) {
    contentTypesXml = contentTypesXml.replace("</Types>", '<Override PartName="/xl/tables/table1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.table+xml"/></Types>');
  }

  zip.file("xl/styles.xml", buildIILStylesXml());
  zip.file(sheetPath, applyIILSheetXmlStyles(sheetXml, rowCount));
  if (invoiceSheet) {
    const invoiceSheetXml = await invoiceSheet.async("string");
    zip.file(invoiceSheetPath, applyIILInvoiceSheetXmlStyles(invoiceSheetXml));
  }
  zip.file(relsPath, relsXml);
  zip.file("xl/tables/table1.xml", tableXml);
  zip.file(contentTypesPath, contentTypesXml);

  return zip.generateAsync({ type: "blob" });
}

export default function IILInvoice() {
  const [masterByCode, setMasterByCode] = useState(new Map());
  const [profileFileName, setProfileFileName] = useState("");
  const [attendanceFile, setAttendanceFile] = useState(null);
  const [attendanceFileName, setAttendanceFileName] = useState("");
  const [approvedAttendanceRows, setApprovedAttendanceRows] = useState([]);
  const [approvedPeriod, setApprovedPeriod] = useState(null);
  const [approvedRunTypes, setApprovedRunTypes] = useState([]);
  const [approvedMonthKey, setApprovedMonthKey] = useState(() => localStorage.getItem("iilActivePayrollMonth") || "");
  const [advanceFileName, setAdvanceFileName] = useState("");
  const [advanceByCode, setAdvanceByCode] = useState(new Map());
  const [advanceLedger, setAdvanceLedger] = useState({ advances: [], recoveries: [] });
  const [advanceOverrides, setAdvanceOverrides] = useState({});
  const [payrollWorkspaces, setPayrollWorkspaces] = useState([]);
  const [payrollNotice, setPayrollNotice] = useState("");
  const [segments, setSegments] = useState([]);
  const [payrollSegments, setPayrollSegments] = useState([]);
  const [payrollInvoiceNumbers, setPayrollInvoiceNumbers] = useState({});
  const [currentBatchAdded, setCurrentBatchAdded] = useState(true);
  const [unmatched, setUnmatched] = useState([]);
  const [period, setPeriod] = useState({ from: "", to: "", days: 0 });
  const [manualDateFrom, setManualDateFrom] = useState("");
  const [manualDateTo, setManualDateTo] = useState("");
  const [invoiceStart, setInvoiceStart] = useState(formatIILInvoiceNumber(IIL_INVOICE_START_NUMBER));
  const [invoiceStartEdited, setInvoiceStartEdited] = useState(false);
  // Defaults to today, but is editable so a past month's invoice (e.g.
  // catching up on July/August) can be approved with its real invoice date
  // instead of the date it happens to be approved on — this date feeds
  // Accounts Receivable's due-date/aging math (Stage 2), so it matters.
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reservedInvoiceNumbers, setReservedInvoiceNumbers] = useState([]);
  const [approvedInvoiceNumbers, setApprovedInvoiceNumbers] = useState({});
  const [invoiceRegistryEntries, setInvoiceRegistryEntries] = useState([]);
  const [completedPayrollRows, setCompletedPayrollRows] = useState(null);
  const [payrollCompletedAt, setPayrollCompletedAt] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const refreshAdvanceLedger = async () => {
    try { setAdvanceLedger(await loadAdvanceLedger()); } catch (error) { /* payroll still works; recorded advances just will not appear */ }
  };
  useEffect(() => { refreshAdvanceLedger(); }, []);

  const stampText = (value) => (value?.toDate ? value.toDate().toLocaleString("en-GB") : "");
  const [lastPayrollDownload, setLastPayrollDownload] = useState("");

  const applyWorkspace = (workspace) => {
    const entries = workspace.payrollEntries || [];
    const resumedMonthKey = workspace.monthKey || workspace.id;
    setApprovedMonthKey(resumedMonthKey);
    if (workspace.status === "IN_PROGRESS") localStorage.setItem("iilActivePayrollMonth", resumedMonthKey);
    setPayrollSegments(payrollSegmentsFromEntries(entries));
    setPayrollInvoiceNumbers(workspace.segmentInvoiceNumbers || {});
    setAdvanceOverrides({});
    setLastPayrollDownload(workspace.lastPayrollDownloadedAt ? `${stampText(workspace.lastPayrollDownloadedAt)} by ${workspace.lastPayrollDownloadedBy || "—"}` : "");
    if (workspace.status === "PAYROLL_COMPLETED") {
      setCompletedPayrollRows(workspace.payrollRows || []);
      setPayrollCompletedAt(workspace.completedAt?.toDate?.().toLocaleString("en-GB") || "Previously completed");
      setMessage(`Loaded completed payroll for ${resumedMonthKey}. Click Reopen Payroll below before generating a revised invoice.`);
    } else {
      setCompletedPayrollRows(null);
      setPayrollCompletedAt("");
      setMessage(`Opened the ${resumedMonthKey} payroll with ${entries.length} employee(s). Use "Download Payroll Excel" to hand it to Finance.`);
    }
  };

  const refreshPayrollWorkspaces = async () => {
    try {
      const snapshot = await getDocs(collection(db, "iil_monthly_invoice_workspaces"));
      setPayrollWorkspaces(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))
        .filter((workspace) => ["IN_PROGRESS", "PAYROLL_COMPLETED"].includes(workspace.status))
        .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0)));
    } catch (error) { /* the list is a convenience; payroll itself is unaffected */ }
  };

  const openPayrollMonth = (workspace) => {
    if (segments.length && !currentBatchAdded && !window.confirm("The invoices you just generated are not added to payroll yet. Opening another month will discard them from this screen. Continue?")) return;
    setSegments([]);
    applyWorkspace(workspace);
  };

  const profileCount = useMemo(() => masterByCode.size, [masterByCode]);
  const masterByName = useMemo(() => buildMasterByName(masterByCode), [masterByCode]);
  const masterByClientEmpId = useMemo(() => {
    const byEmpId = new Map();
    masterByCode.forEach((employee) => {
      const id = normalizeCode(employee.clientEmpId);
      if (id) byEmpId.set(id, employee);
    });
    return byEmpId;
  }, [masterByCode]);
  const totalValue = useMemo(
    () => segments.reduce((sum, segment) => sum + segment.summary.totalInvoiceValue, 0),
    [segments]
  );
  const format = (value) => Math.round(Number(value || 0)).toLocaleString("en-PK");

  const nextInvoiceNumber = (index) => {
    if (reservedInvoiceNumbers[index]) return reservedInvoiceNumbers[index];
    const match = invoiceStart.match(/^(.*?)(\d+)$/);
    if (!match) return `${invoiceStart}-${index + 1}`;
    const prefix = match[1];
    const number = Number(match[2]) + index;
    return `${prefix}${String(number).padStart(match[2].length, "0")}`;
  };
  const allCurrentInvoicesApproved = segments.length > 0 && segments.every((segment, index) =>
    approvedInvoiceNumbers[segment.name] === nextInvoiceNumber(index)
  );
  // Advances recorded on the Advances tab are recovered automatically, but only
  // inside a monthly payroll workspace (the month key ties each recovery to its payroll).
  const advancePlan = useMemo(() => {
    if (!approvedMonthKey) return { byCode: new Map(), notInPayroll: [] };
    const baseRows = buildIILPayrollRows(payrollSegments, () => "", advanceByCode);
    const payrollByCode = new Map();
    baseRows.forEach((row) => {
      payrollByCode.set(normalizeCode(row["EMPLOYEE ID"]), { gross: row["Gross Salary"], eobi: row["EOBI Deduction"], otherDeduction: row.Advance });
    });
    return planRecoveries({
      positions: advancePositions({ advances: advanceLedger.advances, recoveries: advanceLedger.recoveries, excludeMonthKey: approvedMonthKey }),
      payrollByCode,
      overrides: advanceOverrides
    });
  }, [approvedMonthKey, payrollSegments, advanceByCode, advanceLedger, advanceOverrides]);
  const draftPayrollRows = useMemo(
    () => buildIILPayrollRows(
      payrollSegments,
      (index) => payrollInvoiceNumbers[payrollSegments[index]?.name] || "",
      advanceByCode,
      advancePlan
    ),
    [payrollSegments, payrollInvoiceNumbers, advanceByCode, advancePlan]
  );
  const payrollRows = completedPayrollRows || draftPayrollRows;
  const payrollSummary = useMemo(() => summarizePayrollRows(payrollRows), [payrollRows]);
  const payrollIsCompleted = Boolean(completedPayrollRows);
  const hasPayrollDifference = round(payrollSummary.totalPayableDifference) !== 0;
  const poSummaryRows = useMemo(() => IIL_PO_LIMITS.map((poLimit, index) => {
    const currentIndex = segments.findIndex((segment) => matchesPoSegment(segment.name, poLimit));
    const currentSegment = currentIndex >= 0 ? segments[currentIndex] : null;
    const savedInvoice = invoiceRegistryEntries
      .filter((entry) => (!approvedMonthKey || entry.monthKey === approvedMonthKey) && matchesPoSegment(entry.segmentName, poLimit))
      .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0))[0];
    const invoiceAmount = currentSegment
      ? round(currentSegment.summary.invoiceAmount)
      : savedInvoice ? round(savedInvoice.summary?.invoiceAmount) : null;
    const difference = invoiceAmount == null ? null : invoiceAmount - poLimit.allowedAmount;
    return {
      srNo: index + 1,
      ...poLimit,
      invoiceNumber: currentSegment ? nextInvoiceNumber(currentIndex) : savedInvoice?.invoiceNumber || "",
      invoiceDate: currentSegment
        ? new Date().toLocaleDateString("en-GB")
        : savedInvoice?.invoiceDate ? firestoreDate(savedInvoice.invoiceDate) : firestoreDate(savedInvoice?.approvedAt),
      invoiceAmount,
      difference,
      approved: Boolean(savedInvoice) || Boolean(currentSegment && approvedInvoiceNumbers[currentSegment.name])
    };
  }), [segments, invoiceRegistryEntries, approvedMonthKey, approvedInvoiceNumbers, reservedInvoiceNumbers, invoiceStart]);
  const poSummaryTotals = useMemo(() => poSummaryRows.reduce((totals, row) => ({
    allowed: totals.allowed + row.allowedAmount,
    invoiced: totals.invoiced + (row.invoiceAmount || 0),
    excess: totals.excess + Math.max(row.difference || 0, 0)
  }), { allowed: 0, invoiced: 0, excess: 0 }), [poSummaryRows]);

  const loadNextIILInvoiceNumber = async () => {
    try {
      const snapshot = await getDoc(doc(db, "invoice_number_counters", IIL_COUNTER_ID));
      const nextNumber = snapshot.exists()
        ? Math.max(Number(snapshot.data().lastNumber || IIL_INVOICE_START_NUMBER - 1) + 1, IIL_INVOICE_START_NUMBER)
        : IIL_INVOICE_START_NUMBER;
      setInvoiceStart(formatIILInvoiceNumber(nextNumber));
      setInvoiceStartEdited(false);
    } catch (error) {
      setMessage(`Could not load IIL invoice counter: ${error.message}`);
    }
  };

  const applyLiveEmployeeMaster = (snapshot) => {
    const importedMaster = new Map();
    snapshot.docs.forEach((item) => {
      const employee = item.data();
      if (employee.active === false) return;
      const code = normalizeCode(employee.employeeCode || employee.code);
      if (!code) return;
      importedMaster.set(code, {
        ...employee,
        code,
        clientEmpId: normalizeCode(employee.clientEmpId),
        name: employee.name || "",
        salary: toNumber(employee.salary),
        segment: employee.segment || segmentForEmployee(code, employee.department || "")
      });
    });
    setMasterByCode(importedMaster);
    setProfileFileName(`Live IIL Employee Master (${importedMaster.size} active)`);
  };

  useEffect(() => {
    loadNextIILInvoiceNumber();
    const unsubscribe = onSnapshot(
      collection(db, "iil_employee_mappings"),
      (snapshot) => applyLiveEmployeeMaster(snapshot),
      (error) => setMessage(`Could not update the live IIL Employee Master: ${error.message}`)
    );
    const savedRun = sessionStorage.getItem("iilApprovedAttendanceRun");
    if (savedRun) {
      const loadMonthlyRuns = async () => {
        try {
        const run = JSON.parse(savedRun);
        const periodEnd = parseDateInput(run.periodEnd);
        const periodFrom = periodEnd ? new Date(periodEnd.getFullYear(), periodEnd.getMonth() - 1, 21) : null;
        const monthKey = String(run.periodEnd || "").slice(0, 7);
        if (!monthKey) throw new Error("The approved run does not contain a valid month.");
        const snapshots = await Promise.all([
          getDoc(doc(db, "iil_attendance_runs", `${monthKey}_SAFDAR`)),
          getDoc(doc(db, "iil_attendance_runs", `${monthKey}_MANUAL`)),
          getDoc(doc(db, "iil_monthly_invoice_workspaces", monthKey))
        ]);
        const monthlyRuns = snapshots.slice(0, 2)
          .filter((snapshot) => snapshot.exists() && snapshot.data().status === "APPROVED_FOR_INVOICE")
          .map((snapshot) => snapshot.data());
        const combinedRows = monthlyRuns.flatMap((monthlyRun) => (monthlyRun.rows || []).map((row) => ({
          ...row,
          source: monthlyRun.runType
        })));
        const codeCounts = new Map();
        combinedRows.forEach((row) => {
          const code = normalizeCode(row.employeeCode);
          if (code) codeCounts.set(code, (codeCounts.get(code) || 0) + 1);
        });
        const duplicateCodes = [...codeCounts.entries()].filter(([, count]) => count > 1).map(([code]) => code);
        if (duplicateCodes.length) throw new Error(`Monthly consolidation stopped: duplicate Employee Code(s) ${duplicateCodes.join(", ")} exist across Safdar and Manual runs.`);
        const runTypes = monthlyRuns.map((monthlyRun) => monthlyRun.runType);
        setApprovedAttendanceRows(combinedRows);
        setApprovedRunTypes(runTypes);
        setApprovedMonthKey(monthKey);
        localStorage.setItem("iilActivePayrollMonth", monthKey);
        const workspace = snapshots[2].exists() ? snapshots[2].data() : {};
        const savedPayrollEntries = workspace.payrollEntries || [];
        setPayrollSegments(payrollSegmentsFromEntries(savedPayrollEntries));
        setPayrollInvoiceNumbers(workspace.segmentInvoiceNumbers || {});
        setAttendanceFileName(`${monthKey} combined attendance (${runTypes.join(" + ") || "no approved runs"})`);
        if (periodFrom && periodEnd) {
          setApprovedPeriod({ from: periodFrom, to: periodEnd, days: daysInclusive(periodFrom, periodEnd) });
        }
        const missingTypes = ["SAFDAR", "MANUAL"].filter((type) => !runTypes.includes(type));
        setMessage(missingTypes.length
          ? `${combinedRows.length} approved ${monthKey} attendance row(s) loaded. Waiting for the ${missingTypes.join(" and ")} run before combined invoice and payroll generation.`
          : `${combinedRows.length} employees consolidated from approved Safdar and Manual runs for ${monthKey}. Ready for combined invoices and payroll.`);
        sessionStorage.removeItem("iilApprovedAttendanceRun");
      } catch (error) {
        setMessage(`Could not load approved IIL attendance run: ${error.message}`);
      }
      };
      loadMonthlyRuns();
    } else {
      setMessage("Connecting to the live IIL Employee Master. You may upload Safdar attendance directly.");
      // Always reload the saved monthly payroll, even when the browser remembers an active month —
      // otherwise a page opened fresh would never show a payroll that is already saved.
      getDocs(collection(db, "iil_monthly_invoice_workspaces")).then((snapshot) => {
        const open = snapshot.docs
          .map((item) => ({ id: item.id, ...item.data() }))
          .filter((workspace) => ["IN_PROGRESS", "PAYROLL_COMPLETED"].includes(workspace.status))
          .sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
        setPayrollWorkspaces(open);
        const remembered = approvedMonthKey ? open.find((workspace) => (workspace.monthKey || workspace.id) === approvedMonthKey) : null;
        const latest = remembered || open.find((workspace) => workspace.status === "IN_PROGRESS") || open[0];
        if (latest) applyWorkspace(latest);
      }).catch((error) => setMessage(`Could not resume the active payroll workspace: ${error.message}`));
    }
    return unsubscribe;
  }, []);

  useEffect(() => onSnapshot(
    collection(db, "iil_invoice_registry"),
    (snapshot) => setInvoiceRegistryEntries(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
    (error) => setMessage(`Could not load the IIL PO summary: ${error.message}`)
  ), []);

  const proposeIILInvoiceNumbers = async (count) => {
    if (!count) return [];
    const snapshot = await getDoc(doc(db, "invoice_number_counters", IIL_COUNTER_ID));
    const seedNumber = Math.max(parseTrailingNumber(invoiceStart) || IIL_INVOICE_START_NUMBER, IIL_INVOICE_START_NUMBER);
    const start = invoiceStartEdited
      ? seedNumber
      : Math.max(Number(snapshot.data()?.lastNumber || IIL_INVOICE_START_NUMBER - 1) + 1, IIL_INVOICE_START_NUMBER);
    return Array.from({ length: count }, (_, index) => formatIILInvoiceNumber(start + index));
  };

  const proposeMonthlySegmentInvoiceNumbers = async (segmentNames) => {
    if (!segmentNames.length) return [];
    return proposeIILInvoiceNumbers(segmentNames.length);
  };

  const updateProposedInvoiceNumber = (index, value) => {
    setReservedInvoiceNumbers((current) => current.map((number, itemIndex) => itemIndex === index ? value.trim().toUpperCase() : number));
    setApprovedInvoiceNumbers((current) => {
      const next = { ...current };
      delete next[segments[index]?.name];
      return next;
    });
  };

  const approveInvoiceNumber = async (segment, index) => {
    const invoiceNumber = nextInvoiceNumber(index).trim().toUpperCase();
    if (!new RegExp(`^IIL-${IIL_FINANCIAL_YEAR_CODE}-\\d{4,}$`).test(invoiceNumber)) {
      setMessage(`Invoice number must use the format ${IIL_INVOICE_PREFIX}1001.`);
      return;
    }
    if (reservedInvoiceNumbers.some((number, numberIndex) => numberIndex !== index && number === invoiceNumber)) {
      setMessage(`${invoiceNumber} is duplicated in the current batch. Enter a unique number.`);
      return;
    }
    if (!approvedMonthKey) {
      setMessage("A valid invoice month is required before approval.");
      return;
    }
    try {
      setLoading(true);
      await assertPeriodOpen(approvedMonthKey);
      let outcome = "approved";
      await runWithCorrectionPrompt(({ force, correctionReason }) => runTransaction(db, async (transaction) => {
        outcome = "approved";
        const registryRef = doc(db, "iil_invoice_registry", invoiceNumber);
        const counterRef = doc(db, "invoice_number_counters", IIL_COUNTER_ID);
        const workspaceRef = doc(db, "iil_monthly_invoice_workspaces", approvedMonthKey);
        const [registrySnapshot, counterSnapshot, workspaceSnapshot] = await Promise.all([
          transaction.get(registryRef),
          transaction.get(counterRef),
          transaction.get(workspaceRef)
        ]);
        const existing = registrySnapshot.data();
        if (registrySnapshot.exists() && (existing.monthKey !== approvedMonthKey || existing.segmentName !== segment.name)) {
          throw new Error(`${invoiceNumber} has already been approved for ${existing.segmentName || "another segment"} in ${existing.monthKey || "another month"}.`);
        }
        // Re-clicking approve for the SAME segment/month (e.g. after re-uploading the attendance
        // batch) with identical figures is a no-op, not a conflict. With different figures the
        // invoice has changed after approval: that needs an explicit correction with a reason
        // (the approved snapshot is locked by the security rules otherwise).
        let correctionRecord = null;
        if (registrySnapshot.exists() && existing.status === "APPROVED") {
          const newFigures = {
            invoiceAmount: segment.summary.invoiceAmount,
            gstAmount: segment.summary.gst,
            grossAmount: segment.summary.totalInvoiceValue
          };
          const changes = figureChanges([["invoiceAmount", "Invoice amount"], ["gstAmount", "GST"], ["grossAmount", "Invoice total"]], existing.summary, newFigures);
          if (!changes.length) {
            outcome = "unchanged";
            return;
          }
          if (!force) {
            throw new InvoiceOverrideRequiredError({ invoiceNumber, existingAmount: existing.summary?.grossAmount, newAmount: newFigures.grossAmount, changes });
          }
          correctionRecord = buildCorrectionRecord({
            invoiceNumber, clientCode: "IIL", monthKey: approvedMonthKey, previousAmount: existing.summary?.grossAmount, newAmount: newFigures.grossAmount,
            changes, reason: correctionReason, user: auth.currentUser, correctedAt: serverTimestamp()
          });
          outcome = "corrected";
        }
        const workspace = workspaceSnapshot.data() || {};
        const currentSegmentNumber = workspace.segmentInvoiceNumbers?.[segment.name];
        if (currentSegmentNumber && currentSegmentNumber !== invoiceNumber && String(currentSegmentNumber).startsWith(IIL_INVOICE_PREFIX)) {
          throw new Error(`${segment.name} is already locked to approved invoice ${currentSegmentNumber}.`);
        }
        const numericPart = parseTrailingNumber(invoiceNumber);
        const currentLast = Number(counterSnapshot.data()?.lastNumber || IIL_INVOICE_START_NUMBER - 1);
        if (!registrySnapshot.exists() && numericPart !== currentLast + 1) {
          throw new Error(`The next approvable invoice number is ${formatIILInvoiceNumber(currentLast + 1)}. This prevents number gaps.`);
        }
        const grossAmount = round(segment.summary.totalInvoiceValue);
        const gstAmount = round(segment.summary.gst);
        const clientWhtDeduction = round(grossAmount * 0.07);
        const gstWithheld = round(gstAmount * 0.20);
        const effectiveInvoiceDate = existing?.invoiceDate || invoiceDate;
        const taxProfile = await getActiveProfileInTransaction(transaction, "IIL");
        const snapshotFields = buildSnapshotFields({
          clientCode: "IIL",
          sourceCollection: "iil_invoice_registry",
          sourceDocId: invoiceNumber,
          invoiceNumber,
          monthKey: approvedMonthKey,
          accountingPeriod: approvedMonthKey,
          invoiceDate: effectiveInvoiceDate,
          taxProfile,
          sourceDoc: segment.summary
        });
        transaction.set(registryRef, {
          invoiceNumber,
          financialYearCode: IIL_FINANCIAL_YEAR_CODE,
          monthKey: approvedMonthKey,
          segmentName: segment.name,
          status: "APPROVED",
          invoiceDate: effectiveInvoiceDate,
          summary: {
            invoiceAmount: round(segment.summary.invoiceAmount),
            gstAmount,
            grossAmount,
            clientWhtDeduction,
            gstWithheld,
            expectedReceipt: grossAmount - clientWhtDeduction - gstWithheld
          },
          ...(correctionRecord ? { correctionReason: correctionRecord.reason, correctedAtIso: new Date().toISOString() } : {}),
          approvedBy: existing?.approvedBy || auth.currentUser?.uid || null,
          approvedAt: existing?.approvedAt || serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null,
          updatedAt: serverTimestamp()
        }, { merge: true });
        transaction.set(counterRef, {
          client: "IIL",
          prefix: IIL_INVOICE_PREFIX,
          lastNumber: Math.max(currentLast, numericPart),
          lastInvoiceNumber: formatIILInvoiceNumber(Math.max(currentLast, numericPart)),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null
        }, { merge: true });
        transaction.set(workspaceRef, {
          monthKey: approvedMonthKey,
          status: workspace.status || "IN_PROGRESS",
          segmentInvoiceNumbers: { ...(workspace.segmentInvoiceNumbers || {}), [segment.name]: invoiceNumber },
          ...(correctionRecord ? {
            invoiceReconciliation: {
              ...(workspace.invoiceReconciliation || {}),
              [invoiceNumber]: {
                invoiceNumber, segmentName: segment.name, invoiceAmount: round(segment.summary.invoiceAmount), gstAmount, grossAmount,
                clientWhtDeduction, gstWithheld, expectedReceipt: grossAmount - clientWhtDeduction - gstWithheld, updatedAtIso: new Date().toISOString()
              }
            }
          } : {}),
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.uid || null
        }, { merge: true });
        if (correctionRecord) {
          // Marks the write as an authorized correction (the snapshot rule requires a reason).
          snapshotFields.correctionReason = correctionRecord.reason;
          transaction.set(snapshotDocRef(invoiceNumber), snapshotFields, { merge: true });
          transaction.set(doc(collection(db, "invoice_snapshot_corrections")), correctionRecord);
        } else {
          transaction.set(snapshotDocRef(invoiceNumber), snapshotFields);
        }
      }));
      setApprovedInvoiceNumbers((current) => ({ ...current, [segment.name]: invoiceNumber }));
      setMessage(outcome === "unchanged"
        ? `${invoiceNumber} is already approved with exactly these figures — nothing to change.`
        : outcome === "corrected"
          ? `${invoiceNumber} corrected for ${segment.name}. The receivable and P&L now use the new figures.`
          : `${invoiceNumber} approved and permanently locked to ${segment.name}.`);
      await loadNextIILInvoiceNumber();
    } catch (error) {
      setMessage(error.message || "Invoice approval failed.");
    } finally {
      setLoading(false);
    }
  };

  const accumulateMonthlyPayroll = async (processedSegments, invoiceNumbers, monthKey) => runTransaction(db, async (transaction) => {
    const workspaceRef = doc(db, "iil_monthly_invoice_workspaces", monthKey);
    const workspaceSnapshot = await transaction.get(workspaceRef);
    const workspace = workspaceSnapshot.exists() ? workspaceSnapshot.data() : {};
    if (workspace.status === "PAYROLL_COMPLETED") throw new Error(`Payroll for ${monthKey} is already completed.`);
    const incomingCodes = new Set(processedSegments.flatMap((segment) => segment.rows.map((row) => normalizeCode(row["Employee Code"]))));
    const retainedEntries = (workspace.payrollEntries || []).filter((entry) => !incomingCodes.has(normalizeCode(entry.employeeCode)));
    const incomingEntries = processedSegments.flatMap((segment, segmentIndex) => segment.rows.map((row) => ({
      employeeCode: normalizeCode(row["Employee Code"]),
      segmentName: segment.name,
      invoiceNumber: invoiceNumbers[segmentIndex] || "",
      row
    })));
    const payrollEntries = [...retainedEntries, ...incomingEntries];
    const invoiceNumberMap = {
      ...(workspace.segmentInvoiceNumbers || {}),
      ...Object.fromEntries(processedSegments.map((segment, index) => [segment.name, invoiceNumbers[index] || ""]))
    };
    const invoiceReconciliation = {
      ...(workspace.invoiceReconciliation || {}),
      ...Object.fromEntries(processedSegments.map((segment, index) => {
        const invoiceNumber = invoiceNumbers[index] || "";
        const grossAmount = round(segment.summary.totalInvoiceValue);
        const gstAmount = round(segment.summary.gst);
        const clientWhtDeduction = round(grossAmount * 0.07);
        const gstWithheld = round(gstAmount * 0.20);
        return [invoiceNumber, {
          invoiceNumber,
          segmentName: segment.name,
          invoiceAmount: round(segment.summary.invoiceAmount),
          gstAmount,
          grossAmount,
          clientWhtDeduction,
          gstWithheld,
          expectedReceipt: grossAmount - clientWhtDeduction - gstWithheld,
          updatedAtIso: new Date().toISOString()
        }];
      }))
    };
    transaction.set(workspaceRef, {
      monthKey,
      status: "IN_PROGRESS",
      payrollEntries,
      payrollEmployeeCount: payrollEntries.length,
      segmentInvoiceNumbers: invoiceNumberMap,
      invoiceReconciliation,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || null
    }, { merge: true });
    return { payrollEntries, invoiceNumberMap };
  });

  const getToken = async () => {
    const user = auth.currentUser;
    if (!user) {
      alert("Not logged in.");
      return null;
    }
    return user.getIdToken();
  };

  const handleProfileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { cellDates: true });
    const parsed = parseProfileSheet(workbook);

    setMasterByCode(parsed);
    setProfileFileName(file.name);
    setSegments([]);
    setReservedInvoiceNumbers([]);
    setApprovedInvoiceNumbers({});
    setCompletedPayrollRows(null);
    setPayrollCompletedAt("");
    setUnmatched([]);
    setMessage(`${parsed.size} employee profile(s) loaded from Profile Sheet.`);
  };

  const handleAttendanceUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (segments.length && !currentBatchAdded) {
      event.target.value = "";
      setMessage("Add the currently generated invoice batch to payroll before selecting another attendance sheet.");
      return;
    }

    setAttendanceFile(file);
    setApprovedAttendanceRows([]);
    setApprovedPeriod(null);
    setApprovedRunTypes([]);
    setAttendanceFileName(file.name);
    setSegments([]);
    setReservedInvoiceNumbers([]);
    setApprovedInvoiceNumbers({});
    setCompletedPayrollRows(null);
    setPayrollCompletedAt("");
    setUnmatched([]);
    setCurrentBatchAdded(true);
    setMessage("Attendance file selected. Click Generate Invoices.");
  };

  const handleAdvanceUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { cellDates: true });
      const parsed = parseAdvanceSheet(workbook);

      setAdvanceByCode(parsed);
      setAdvanceFileName(file.name);
      setCompletedPayrollRows(null);
      setPayrollCompletedAt("");
      setMessage(`${parsed.size} employee advance row(s) loaded. Payroll draft updated.`);
    } catch (error) {
      setAdvanceByCode(new Map());
      setAdvanceFileName("");
      setCompletedPayrollRows(null);
      setPayrollCompletedAt("");
      setMessage(error.message || "Could not read advance file.");
    }
  };

  const generateInvoices = async () => {
    if (!masterByCode.size) {
      setMessage("The live IIL Employee Master is empty. Add employees from IIL Attendance or upload a Profile Sheet.");
      return;
    }
    if (!attendanceFile && !approvedAttendanceRows.length) {
      setMessage("Upload the attendance sheet first.");
      return;
    }
    if ((manualDateFrom && !manualDateTo) || (!manualDateFrom && manualDateTo)) {
      setMessage("Enter both Date From and Date To, or leave both blank for automatic period detection.");
      return;
    }

    try {
      let attendanceRows;
      let overtimeByCode;
      let detectedPeriod;
      if (approvedAttendanceRows.length) {
        attendanceRows = approvedAttendanceRows.map((row) => ({
          code: normalizeCode(row.employeeCode),
          uploadedName: row.name,
          nameKey: normalizeEmployeeName(row.name),
          sourceSheet: row.source || "Approved IIL attendance run",
          daysWorked: toNumber(row.daysWorked),
          deductions: Math.max((approvedPeriod?.days || 30) - toNumber(row.daysWorked), 0),
          periodDays: approvedPeriod?.days || 30
        }));
        overtimeByCode = new Map();
        approvedAttendanceRows.forEach((row) => {
          const code = normalizeCode(row.employeeCode);
          if (code) overtimeByCode.set(code, toNumber(row.otHours));
          if (row.clientEmpId) overtimeByCode.set(normalizeCode(row.clientEmpId), toNumber(row.otHours));
        });
        detectedPeriod = approvedPeriod || { from: "", to: "", days: attendanceRows[0]?.periodDays || 30 };
      } else {
        const buffer = await attendanceFile.arrayBuffer();
        const workbook = XLSX.read(buffer, { cellDates: true });
        attendanceRows = parseAttendanceRows(workbook);
        overtimeByCode = parseOvertimeByCode(workbook, attendanceRows);
        detectedPeriod = periodFromAttendanceWorkbook(workbook, attendanceRows);
      }
      const manualFrom = parseDateInput(manualDateFrom);
      const manualTo = parseDateInput(manualDateTo);
      const hasManualPeriod = manualFrom && manualTo;
      const nextPeriod = hasManualPeriod
        ? { from: manualFrom, to: manualTo, days: daysInclusive(manualFrom, manualTo) }
        : detectedPeriod;

      if (hasManualPeriod && !isRealPayrollRange(manualFrom, manualTo)) {
        setMessage("Manual Date From / To must be a valid payroll range between 28 and 31 days.");
        return;
      }

      const grouped = {};
      const unmatchedRows = [];

      attendanceRows.forEach((attendance) => {
        const replacement = TEMP_EMPLOYEE_REPLACEMENTS[attendance.code];
        const directMaster = masterByCode.get(attendance.code)
          || masterByClientEmpId.get(attendance.code)
          || masterByName.get(attendance.nameKey);
        const lookupCode = replacement?.sourceCode || attendance.code;
        const replacementMaster = replacement && !directMaster ? masterByCode.get(lookupCode) : null;
        const sourceMaster = directMaster || replacementMaster;
        const master = sourceMaster && replacement && !directMaster
          ? {
              ...sourceMaster,
              code: replacement.code,
              name: replacement.name,
              cardNo: replacement.code
            }
          : sourceMaster;
        if (!master) {
          unmatchedRows.push(attendance);
          return;
        }

        const matchedCode = master.code;
        if (!grouped[master.segment]) grouped[master.segment] = [];
        grouped[master.segment].push({
          ...master,
          daysWorked: attendance.daysWorked,
          deductions: attendance.deductions,
          otHours: overtimeByCode.get(matchedCode)
            ?? overtimeByCode.get(attendance.code)
            ?? overtimeByCode.get(lookupCode)
            ?? overtimeByCode.get(attendance.nameKey)
            ?? 0
        });
      });

      if (unmatchedRows.length) {
        setSegments([]);
        setReservedInvoiceNumbers([]);
        setUnmatched(unmatchedRows);
        setMessage(`${attendanceRows.length} attendance employee(s) received, but ${unmatchedRows.length} were not found in the live IIL Employee Master. Invoice generation stopped; add them in IIL Attendance and try again.`);
        return;
      }

      const processed = Object.entries(grouped).map(([name, rows]) => {
        const detailRows = processSegment(name, rows, nextPeriod.days);
        return { name, rows: detailRows, summary: summarizeRows(detailRows) };
      });
      const payrollMonthKey = approvedMonthKey || (nextPeriod.to instanceof Date
        ? `${nextPeriod.to.getFullYear()}-${String(nextPeriod.to.getMonth() + 1).padStart(2, "0")}`
        : "");
      const reservedNumbers = payrollMonthKey
        ? await proposeMonthlySegmentInvoiceNumbers(processed.map((segment) => segment.name))
        : await proposeIILInvoiceNumbers(processed.length);
      setApprovedMonthKey(payrollMonthKey);
      if (payrollMonthKey) localStorage.setItem("iilActivePayrollMonth", payrollMonthKey);

      setPeriod(nextPeriod);
      setSegments(processed);
      setReservedInvoiceNumbers(reservedNumbers);
      setApprovedInvoiceNumbers({});
      setCurrentBatchAdded(false);
      setCompletedPayrollRows(null);
      setPayrollCompletedAt("");
      if (reservedNumbers.length) setInvoiceStart(reservedNumbers[0]);
      setInvoiceStartEdited(false);
      setUnmatched(unmatchedRows);
      const matchedCount = attendanceRows.length - unmatchedRows.length;
      setMessage(`${attendanceRows.length} attendance row(s) read. ${matchedCount} matched and ${processed.length} invoice segment(s) are ready. Approve each invoice number before downloading or adding the batch to payroll.`);
    } catch (error) {
      setSegments([]);
      setReservedInvoiceNumbers([]);
      setApprovedInvoiceNumbers({});
      setCompletedPayrollRows(null);
      setPayrollCompletedAt("");
      setMessage(error.message || "Could not read attendance file.");
    }
  };

  const addCurrentBatchToPayroll = async () => {
    if (!segments.length || !reservedInvoiceNumbers.length) {
      setMessage("Generate the current invoice batch before adding it to payroll.");
      return;
    }
    if (!allCurrentInvoicesApproved) {
      setMessage("Approve and save every invoice number before adding this batch to payroll.");
      return;
    }
    if (!approvedMonthKey) {
      setMessage("The payroll month could not be detected. Set a valid Date From and Date To, then generate again.");
      return;
    }
    try {
      setLoading(true);
      const accumulated = await accumulateMonthlyPayroll(segments, reservedInvoiceNumbers, approvedMonthKey);
      setPayrollSegments(payrollSegmentsFromEntries(accumulated.payrollEntries));
      setPayrollInvoiceNumbers(accumulated.invoiceNumberMap);
      setCurrentBatchAdded(true);
      setMessage(`${segments.reduce((count, segment) => count + segment.rows.length, 0)} employee(s) from the current invoice batch were added. The ${approvedMonthKey} payroll draft now contains ${accumulated.payrollEntries.length} employee(s).`);
    } catch (error) {
      setMessage(`Could not add the current batch to payroll: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const markPayrollCompleted = async () => {
    const fail = (text) => { setPayrollNotice(text); setMessage(text); };
    setPayrollNotice("");
    if (segments.length && !currentBatchAdded) {
      fail("Add the current invoice batch to payroll before completing the month.");
      return;
    }
    if (!draftPayrollRows.length) {
      fail("Generate IIL invoices before completing payroll.");
      return;
    }
    if (!advanceFileName && !advancePlan.byCode.size) {
      const proceed = window.confirm("No advance / deduction sheet is uploaded and no recorded advances are being recovered this month. Complete payroll with only the EOBI deduction?");
      if (!proceed) return;
    }

    if (approvedMonthKey) {
      const payrollCodes = new Set(draftPayrollRows.map((row) => normalizeCode(row["EMPLOYEE ID"])).filter(Boolean));
      const missingActiveEmployees = [...masterByCode.values()].filter((employee) => !payrollCodes.has(normalizeCode(employee.code)));
      if (missingActiveEmployees.length) {
        const names = missingActiveEmployees.map((employee) => `${employee.code} ${employee.name || ""}${employee.segment ? ` (${employee.segment})` : ""}`.trim()).join("; ");
        fail(`Payroll cannot be completed: ${missingActiveEmployees.length} active employee(s) have no attendance in this month's payroll — ${names}. `
          + "If they have left, mark them Inactive in IIL Attendance (Employee Master). If they worked, add their attendance, generate the invoice batch and add it to payroll.");
        return;
      }
    }
    try {
      const completedAt = new Date().toLocaleString("en-GB");
      const snapshotRows = draftPayrollRows.map((row) => ({ ...row }));
      if (approvedMonthKey) {
        await setDoc(doc(db, "iil_monthly_invoice_workspaces", approvedMonthKey), {
          monthKey: approvedMonthKey,
          status: "PAYROLL_COMPLETED",
          payrollRows: snapshotRows,
          payrollEmployeeCount: snapshotRows.length,
          completedAt: serverTimestamp(),
          completedBy: auth.currentUser?.email || auth.currentUser?.uid || null
        }, { merge: true });
        await postRecoveries(approvedMonthKey, advancePlan);
        await refreshAdvanceLedger();
        await refreshPayrollWorkspaces();
      }
      setCompletedPayrollRows(snapshotRows);
      setPayrollCompletedAt(completedAt);
      localStorage.removeItem("iilActivePayrollMonth");
      const buffer = buildIILPayrollWorkbook(snapshotRows, payrollSegments, period, completedAt);
      saveAs(
        new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
        `IIL_Payroll_Completed_${cleanFileName(periodText(period))}.xlsx`
      );
      setPayrollNotice("");
      setMessage(`Combined payroll completed and downloaded with ${draftPayrollRows.length} employee row(s). The monthly workspace is now locked.`);
    } catch (error) {
      fail(`Could not complete payroll: ${error.message}`);
    }
  };

  const reopenPayrollDraft = async () => {
    if (!approvedMonthKey) {
      setMessage("Could not identify the completed payroll month to reopen.");
      return;
    }
    try {
      await setDoc(doc(db, "iil_monthly_invoice_workspaces", approvedMonthKey), {
        status: "IN_PROGRESS",
        reopenedAt: serverTimestamp(),
        reopenedBy: auth.currentUser?.email || auth.currentUser?.uid || null,
        updatedAt: serverTimestamp()
      }, { merge: true });
      await reverseRecoveries(approvedMonthKey);
      await refreshAdvanceLedger();
      await refreshPayrollWorkspaces();
      localStorage.setItem("iilActivePayrollMonth", approvedMonthKey);
      setCompletedPayrollRows(null);
      setPayrollCompletedAt("");
      setCurrentBatchAdded(true);
      setMessage(`${approvedMonthKey} payroll reopened. Upload the revised attendance sheet, generate the revised invoice, and add that batch to payroll again.`);
    } catch (error) {
      setMessage(`Could not reopen payroll: ${error.message}`);
    }
  };

  const downloadPayrollExcel = () => {
    if (!payrollRows.length) {
      setMessage("Generate IIL invoices before downloading payroll.");
      return;
    }

    const buffer = buildIILPayrollWorkbook(payrollRows, payrollSegments, period, payrollCompletedAt);
    const status = payrollIsCompleted ? "Completed" : "Draft";
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `IIL_Payroll_${status}_${approvedMonthKey || cleanFileName(periodText(period))}.xlsx`
    );
    if (approvedMonthKey) {
      // Does not touch updatedAt, so a download never changes which month the page resumes.
      setDoc(doc(db, "iil_monthly_invoice_workspaces", approvedMonthKey), {
        lastPayrollDownloadedAt: serverTimestamp(),
        lastPayrollDownloadedBy: auth.currentUser?.email || null,
        payrollDownloadCount: increment(1)
      }, { merge: true })
        .then(() => { setLastPayrollDownload(`${new Date().toLocaleString("en-GB")} by ${auth.currentUser?.email || "—"}`); return refreshPayrollWorkspaces(); })
        .catch(() => {});
    }
  };

  const downloadProfileTemplate = () => {
    const rows = [
      [
        "Employee Code",
        "Employee Card No",
        "Name of Employee",
        "Father Name",
        "CNIC",
        "Department",
        "Segment",
        "Designation",
        "Date of Joining",
        "Salary",
        "Bank",
        "Account Number",
        "PMT Mode"
      ],
      [
        "10001",
        "9001",
        "Example Employee",
        "Example Father",
        "00000-0000000-0",
        "Production",
        "Pacakging of PPRC",
        "Worker",
        "",
        40000,
        "HBL",
        "00000000000000",
        "HBL"
      ]
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = rows[0].map((header) => ({ wch: Math.max(14, header.length + 4) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "IIL Profile");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "IIL_Profile_Template.xlsx");
  };

  const downloadTemplate = () => {
    const headers = [
      "Employee Code", "Name",
      "21", "22", "23", "24", "25", "26", "27", "28", "29", "30", "31",
      "1", "2", "3", "4", "5", "6", "7", "8", "9", "10",
      "11", "12", "13", "14", "15", "16", "17", "18", "19", "20"
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Attendance");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "IIL_Attendance_Template.xlsx");
  };

  const downloadAdvanceTemplate = () => {
    const rows = [
      ["Employee Code", "Employee Name", "Advance"],
      ["10001", "Example Employee", 0]
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 16 }, { wch: 28 }, { wch: 14 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Advances");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "IIL_Advance_Template.xlsx");
  };

  const downloadExcel = async (segment, index) => {
    if (approvedInvoiceNumbers[segment.name] !== nextInvoiceNumber(index)) {
      setMessage("Approve and save this invoice number before downloading the invoice.");
      return;
    }
    const keys = [
      "Employee Code", "Card No", "Name", "Segment", "DOJ",
      "Salary", "Month Days", "Days Worked",
      "P/D Rate", "Salary Amount", "OT Hours", "OT Amount",
      "PESSI", "EOBI", "Education Cess", "Group Life Insurance",
      ...(segment.rows.some((row) => "Transport Cost" in row) ? ["Transport Cost"] : []),
      "Total Cost", "Service Charges", "With Holding Tax",
      "Invoice Amount", "GST Tax 16%", "Total Invoice Value"
    ];
    const data = [
      [],
      ["", "", "ALM OUTSOURCING SERVICES PVT LTD"],
      ["", "", "INVOICE WORKING"],
      ["", "", `For the month of ${periodText(period)}`],
      [],
      keys,
      ...segment.rows.map((row) => keys.map((key) => row[key] ?? ""))
    ];
    const totals = keys.map((key, index) => {
      if (index === 0) return "TOTAL";
      const total = segment.rows.reduce((sum, row) => sum + toNumber(row[key], 0), 0);
      return total ? Math.round(total) : "";
    });
    data.push(totals);

    const ws = XLSX.utils.aoa_to_sheet(data);
    ws["!cols"] = keys.map((key) => ({ wch: Math.max(14, Math.min(26, key.length + 4)) }));
    ws["!merges"] = [
      { s: { r: 1, c: 2 }, e: { r: 1, c: 7 } },
      { s: { r: 2, c: 2 }, e: { r: 2, c: 7 } },
      { s: { r: 3, c: 2 }, e: { r: 3, c: 7 } }
    ];
    ws["!autofilter"] = {
      ref: XLSX.utils.encode_range({ s: { r: 5, c: 0 }, e: { r: data.length - 1, c: keys.length - 1 } })
    };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Current details");
    XLSX.utils.book_append_sheet(wb, buildIILInvoiceSheet(segment, period, nextInvoiceNumber(index)), "Invoice");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
    const styledBlob = await styleIILWorkbookBuffer(buffer, data, keys);
    saveAs(styledBlob, `IIL_${cleanFileName(segment.name)}.xlsx`);
  };

  const downloadPDF = async (segment, index) => {
    const token = await getToken();
    if (!token) return null;

    const invoiceNumber = nextInvoiceNumber(index);
    const client = clientDetailsForIILSegment(segment.name);
    try {
      const response = await fetch(`${API_BASE_URL}/invoices/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          invoiceType: "iil",
          invoiceNumber,
          segment: segment.name,
          clientName: client.name,
          clientAddress: client.address,
          clientNTN: `${client.ntn} STRN ${client.strn}`,
          period: {
            from: period.from ? formatDate(period.from) : "",
            to: period.to ? formatDate(period.to) : "",
            label: periodText(period),
            days: period.days
          },
          description: invoiceDescriptionForIILSegment(segment.name, period),
          summary: {
            invoiceAmount: segment.summary.invoiceAmount,
            gst: segment.summary.gst,
            totalInvoiceValue: segment.summary.totalInvoiceValue
          },
          grandTotal: segment.summary.totalInvoiceValue
        })
      });

      if (!response.ok) {
        const text = await response.text();
        alert(text || "PDF generation failed.");
        return null;
      }

      return response.blob();
    } catch (error) {
      alert(`PDF download failed: ${error.message}. Please refresh and try again.`);
      return null;
    }
  };

  const downloadOnePDF = async (segment, index) => {
    if (approvedInvoiceNumbers[segment.name] !== nextInvoiceNumber(index)) {
      setMessage("Approve and save this invoice number before downloading the invoice.");
      return;
    }
    const blob = await downloadPDF(segment, index);
    if (!blob) return;
    saveAs(blob, `${cleanFileName(nextInvoiceNumber(index))}_${cleanFileName(segment.name)}.pdf`);
  };

  const downloadAllPDFs = async () => {
    if (!segments.length) {
      alert("No IIL invoices to download.");
      return;
    }
    if (!allCurrentInvoicesApproved) {
      setMessage("Approve and save every invoice number before downloading all PDFs.");
      return;
    }

    setLoading(true);
    try {
      const zip = new JSZip();
      for (let index = 0; index < segments.length; index += 1) {
        const segment = segments[index];
        const blob = await downloadPDF(segment, index);
        if (blob) zip.file(`${cleanFileName(nextInvoiceNumber(index))}_${cleanFileName(segment.name)}.pdf`, blob);
      }
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, "IIL_Invoices.zip");
    } finally {
      setLoading(false);
    }
  };

  const downloadPoSummaryExcel = () => {
    const title = "IIL Monthly PO Summary";
    const headers = ["Sr. No", "Segment", "PO Amount (Allowed)", "Invoice No", "Invoice Date", "Invoice Amount (Pre-GST)", "Difference", "Remarks"];
    const data = [
      [title],
      ["Month", approvedMonthKey || "Current generated batch"],
      ["Basis", "PO limits are exclusive of 16% GST. Invoice Amount is compared before GST."],
      [],
      headers,
      ...poSummaryRows.map((row) => [
        row.srNo,
        row.segment,
        row.allowedAmount,
        row.invoiceNumber || "",
        summaryDateValue(row.invoiceDate) || "",
        row.invoiceAmount == null ? "" : row.invoiceAmount,
        row.difference == null ? "" : row.difference,
        poSummaryRemark(row)
      ]),
      [],
      ["TOTAL", "", poSummaryTotals.allowed, "", "", poSummaryTotals.invoiced, poSummaryRows.reduce((sum, row) => sum + (row.difference || 0), 0), poSummaryTotals.excess > 0 ? `Total excess PKR ${format(poSummaryTotals.excess)}` : "No PO excess"]
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(data, { cellDates: true });
    worksheet["!cols"] = [
      { wch: 9 }, { wch: 31 }, { wch: 21 }, { wch: 18 },
      { wch: 15 }, { wch: 25 }, { wch: 17 }, { wch: 27 }
    ];
    worksheet["!autofilter"] = { ref: `A5:H${5 + poSummaryRows.length}` };
    for (let row = 6; row <= 5 + poSummaryRows.length; row += 1) {
      ["C", "F", "G"].forEach((column) => {
        if (worksheet[`${column}${row}`]) worksheet[`${column}${row}`].z = "#,##0;[Red](#,##0);-";
      });
      if (worksheet[`E${row}`]?.v instanceof Date) worksheet[`E${row}`].z = "dd/mm/yyyy";
    }
    ["C", "F", "G"].forEach((column) => {
      const totalRow = 7 + poSummaryRows.length;
      if (worksheet[`${column}${totalRow}`]) worksheet[`${column}${totalRow}`].z = "#,##0;[Red](#,##0);-";
    });
    worksheet["!merges"] = [XLSX.utils.decode_range("A1:H1")];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "PO Summary");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellDates: true });
    saveAs(
      new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `IIL_PO_Summary_${approvedMonthKey || new Date().toISOString().slice(0, 7)}.xlsx`
    );
    setMessage("IIL PO summary downloaded in Excel format.");
  };

  const copyPoSummaryForOutlook = async () => {
    const headers = ["Sr. No", "Segment", "PO Amount (Allowed)", "Invoice No", "Invoice Date", "Invoice Amount (Pre-GST)", "Difference", "Remarks"];
    const values = poSummaryRows.map((row) => [
      row.srNo,
      row.segment,
      `PKR ${format(row.allowedAmount)}`,
      row.invoiceNumber || "—",
      row.invoiceDate || "—",
      row.invoiceAmount == null ? "—" : `PKR ${format(row.invoiceAmount)}`,
      row.difference == null ? "—" : `${row.difference > 0 ? "+" : ""}PKR ${format(row.difference)}`,
      poSummaryRemark(row)
    ]);
    const plainText = [
      `IIL Monthly PO Summary${approvedMonthKey ? ` - ${approvedMonthKey}` : ""}`,
      "PO limits and invoice amounts are exclusive of 16% GST.",
      headers.join("\t"),
      ...values.map((row) => row.join("\t"))
    ].join("\n");
    const tableRows = [headers, ...values].map((row, rowIndex) => `<tr>${row.map((cell) => `<${rowIndex === 0 ? "th" : "td"} style="border:1px solid #cbd5e1;padding:7px 9px;${rowIndex === 0 ? "background:#0b3d6d;color:#fff;font-weight:700;" : ""}">${escapeHtml(cell)}</${rowIndex === 0 ? "th" : "td"}>`).join("")}</tr>`).join("");
    const html = `<div style="font-family:Arial,sans-serif"><h3 style="color:#0b3d6d;margin:0 0 6px">IIL Monthly PO Summary${approvedMonthKey ? ` - ${escapeHtml(approvedMonthKey)}` : ""}</h3><p style="margin:0 0 12px">PO limits and invoice amounts are exclusive of 16% GST.</p><table style="border-collapse:collapse;font-size:12px">${tableRows}</table></div>`;
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([new window.ClipboardItem({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([plainText], { type: "text/plain" })
        })]);
      } else {
        await navigator.clipboard.writeText(plainText);
      }
      setMessage("IIL PO summary copied. Paste it directly into Outlook.");
    } catch (error) {
      setMessage(`Could not copy the summary: ${error.message}. Use Download Summary Excel instead.`);
    }
  };

  return (
    <div className="app-shell">
      <Sidebar />

      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Client Invoice</div>
            <h1 className="page-title">Create IIL Invoice</h1>
            <p className="page-copy">
              The live IIL Employee Master supplies employee mapping, salary, segment, and payroll details. Upload Safdar attendance directly or use an approved Attendance run.
            </p>
          </div>
          <div className="metric-card" style={{ minWidth: 240 }}>
            <div className="metric-label">Total Invoice Value</div>
            <div className="metric-value">PKR {format(totalValue)}</div>
            <div className="page-copy" style={{ marginTop: 8 }}>
              {segments.length} segment invoice(s)
            </div>
          </div>
        </header>
        <IILSubnav />

        <section className="panel" style={{ marginBottom: 20 }}>
          <div className="panel-header">
            <div>
              <h2 className="panel-title">Monthly Payroll</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>
                Every month's payroll stays here from the day invoices are added to it. Open a month, then download it for Finance whenever you need — before or after the month is completed.
              </p>
            </div>
            <button type="button" className="btn btn-primary" disabled={!payrollRows.length} onClick={downloadPayrollExcel}>
              Download Payroll Excel{approvedMonthKey && payrollRows.length ? ` (${approvedMonthKey})` : ""}
            </button>
          </div>
          <div className="panel-body table-wrap">
            <table className="data-table">
              <thead><tr><th>Month</th><th>Status</th><th>Employees</th><th>Last updated</th><th>Last downloaded</th><th /></tr></thead>
              <tbody>
                {payrollWorkspaces.map((workspace) => {
                  const monthKey = workspace.monthKey || workspace.id;
                  const isOpen = monthKey === approvedMonthKey;
                  return (
                    <tr key={workspace.id} style={isOpen ? { background: "var(--surface)" } : undefined}>
                      <td><strong>{monthKey}</strong></td>
                      <td>{workspace.status === "PAYROLL_COMPLETED" ? <span className="status-pill success">Completed</span> : <span className="status-pill warning">In progress</span>}</td>
                      <td>{workspace.status === "PAYROLL_COMPLETED" ? (workspace.payrollRows || []).length : (workspace.payrollEntries || []).length}</td>
                      <td>{stampText(workspace.updatedAt) || "—"}</td>
                      <td>{workspace.lastPayrollDownloadedAt ? `${stampText(workspace.lastPayrollDownloadedAt)} (${workspace.lastPayrollDownloadedBy || "—"})` : "Not downloaded yet"}</td>
                      <td>{isOpen ? <span className="badge">Open below</span> : <button type="button" className="btn btn-muted" onClick={() => openPayrollMonth(workspace)}>Open</button>}</td>
                    </tr>
                  );
                })}
                {!payrollWorkspaces.length && <tr><td colSpan={6}>No monthly payroll has been started yet.</td></tr>}
              </tbody>
            </table>
            {lastPayrollDownload && <p className="page-copy" style={{ marginTop: 8 }}>{approvedMonthKey} payroll last downloaded {lastPayrollDownload}.</p>}
          </div>
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2 className="panel-title">IIL Profile and Workbooks</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>
                Employees added or edited in IIL Attendance are available here automatically. Attendance controls days worked and overtime for the 21st to 20th cycle.
              </p>
            </div>
            <span className="badge">Employee Code</span>
          </div>
          <div className="panel-body">
            <div className="form-grid">
              <div>
                <label htmlFor="iil-master" className="file-drop">
                  <div className="file-title">{profileFileName || "Loading live IIL Employee Master"}</div>
                  <div className="file-copy">
                    {profileCount ? `${profileCount} active employee profile(s) linked` : "Optional fallback: upload a Profile Sheet."}
                  </div>
                </label>
                <input id="iil-master" type="file" accept=".xlsx,.xls" onChange={handleProfileUpload} className="hidden-input" />
              </div>

              <div>
                <label htmlFor="iil-attendance" className="file-drop">
                  <div className="file-title">{attendanceFileName || "Choose attendance sheet"}</div>
                  <div className="file-copy">Safdar Emp ID or ALM Employee Code can be used.</div>
                </label>
                <input id="iil-attendance" type="file" accept=".xlsx,.xls" onChange={handleAttendanceUpload} className="hidden-input" />
              </div>

              <div>
                <label htmlFor="iil-advances" className="file-drop">
                  <div className="file-title">{advanceFileName || "Choose advance / deduction sheet"}</div>
                  <div className="file-copy">
                    Optional: other deductions. Advances recorded on the Advances tab are recovered automatically. Needs Employee Code and Advance or Other Deduction.
                  </div>
                </label>
                <input id="iil-advances" type="file" accept=".xlsx,.xls" onChange={handleAdvanceUpload} className="hidden-input" />
              </div>
            </div>

            <div className="form-grid" style={{ marginTop: 18 }}>
              <div className="field">
                <label>Proposed Starting Invoice Number</label>
                <input
                  className="input"
                  value={invoiceStart}
                  onChange={(event) => {
                    setInvoiceStart(event.target.value);
                    setInvoiceStartEdited(true);
                  }}
                />
                <p className="page-copy" style={{ marginTop: 4 }}>
                  Preview only. A number is consumed only after its invoice is approved and saved.
                </p>
              </div>
              <div className="field">
                <label>Invoice Date</label>
                <input className="input" type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} />
                <p className="page-copy" style={{ marginTop: 4 }}>
                  Set this to the real invoice date when catching up on a past month — it drives receivables aging.
                </p>
              </div>
              <div className="field">
                <label>Payroll Period</label>
                <input className="input" value={period.days ? `${periodText(period)} (${period.days} days)` : "Generated after attendance upload"} readOnly />
              </div>
              <div className="field">
                <label>Date From</label>
                <input
                  className="input"
                  type="date"
                  value={manualDateFrom}
                  onChange={(event) => setManualDateFrom(event.target.value)}
                />
              </div>
              <div className="field">
                <label>Date To</label>
                <input
                  className="input"
                  type="date"
                  value={manualDateTo}
                  onChange={(event) => setManualDateTo(event.target.value)}
                />
              </div>
            </div>

            {message && <p className="page-copy">{message}</p>}

            {unmatched.length > 0 && (
              <div className="empty-state" style={{ textAlign: "left", padding: 18 }}>
                <strong>{unmatched.length} attendance row(s) were not found in the Profile Sheet.</strong>
                <p className="page-copy" style={{ marginTop: 8 }}>
                  Fix these employee code/name values in the IIL Profile Sheet or attendance workbook, then generate again.
                </p>
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee Code</th>
                        <th>Employee Name</th>
                        <th>Source Sheet</th>
                        <th>Days Worked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unmatched.map((row, index) => (
                        <tr key={`${row.code || row.uploadedName}-${index}`}>
                          <td>{row.code || "Blank"}</td>
                          <td>{row.uploadedName || "Blank"}</td>
                          <td>{row.sourceSheet || "Attendance"}</td>
                          <td>{row.daysWorked}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="button-row">
              <button type="button" className="btn btn-muted" onClick={downloadProfileTemplate}>
                Download Profile Template
              </button>
              <button type="button" className="btn btn-muted" onClick={downloadTemplate}>
                Download Attendance Template
              </button>
              <button type="button" className="btn btn-muted" onClick={downloadAdvanceTemplate}>
                Download Advance Template
              </button>
              <button type="button" className="btn btn-success" disabled={!segments.length || !allCurrentInvoicesApproved || loading} onClick={downloadAllPDFs}>
                {loading ? "Preparing ZIP..." : "Download All PDFs"}
              </button>
              <button type="button" className="btn btn-muted" disabled={!segments.length || !allCurrentInvoicesApproved || currentBatchAdded || loading} onClick={addCurrentBatchToPayroll}>
                {currentBatchAdded ? "Added to Payroll" : "Add Current Batch to Payroll"}
              </button>
              <button type="button" className="btn btn-success" disabled={!payrollRows.length} onClick={downloadPayrollExcel}>
                Download Payroll Excel
              </button>
              <button type="button" className="btn btn-primary" disabled={!profileCount || (!attendanceFile && !approvedAttendanceRows.length)} onClick={generateInvoices}>
                Generate Invoices
              </button>
            </div>
          </div>
        </section>

        <section className="panel iil-po-summary" style={{ marginTop: 22 }}>
          <div className="panel-header">
            <div>
              <h2 className="panel-title">IIL Monthly PO Summary</h2>
              <p className="page-copy" style={{ marginTop: 4 }}>
                {approvedMonthKey ? `Invoice control for ${approvedMonthKey}. ` : "Current invoice control. "}
                PO limits are compared with the invoice amount before 16% GST.
              </p>
            </div>
            <div className="button-row iil-po-actions">
              <span className={`status-pill ${poSummaryTotals.excess > 0 ? "danger" : "success"}`}>
                {poSummaryTotals.excess > 0 ? `PKR ${format(poSummaryTotals.excess)} over PO` : "No PO excess"}
              </span>
              <button type="button" className="btn btn-muted" onClick={copyPoSummaryForOutlook}>Copy Table for Outlook</button>
              <button type="button" className="btn btn-primary" onClick={downloadPoSummaryExcel}>Download Summary Excel</button>
            </div>
          </div>
          <div className="card-grid iil-po-metrics">
            <div className="metric-card">
              <div className="metric-label">Total PO Allowed</div>
              <div className="metric-value">PKR {format(poSummaryTotals.allowed)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Pre-GST Invoiced</div>
              <div className="metric-value">PKR {format(poSummaryTotals.invoiced)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Amount Exceed</div>
              <div className={`metric-value ${poSummaryTotals.excess > 0 ? "negative" : ""}`}>PKR {format(poSummaryTotals.excess)}</div>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table iil-po-table">
              <thead>
                <tr>
                  <th>Sr. No</th>
                  <th>Segment</th>
                  <th>PO Amount (Allowed)</th>
                  <th>Invoice No</th>
                  <th>Invoice Date</th>
                  <th>Invoice Amount (Pre-GST)</th>
                  <th>Difference</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {poSummaryRows.map((row) => {
                  const hasDifference = row.difference != null && row.difference !== 0;
                  const exceeds = row.difference > 0;
                  return <tr key={row.segment} className={exceeds ? "po-row-exceeds" : hasDifference ? "po-row-difference" : ""}>
                    <td>{row.srNo}</td>
                    <td>{row.segment}</td>
                    <td>PKR {format(row.allowedAmount)}</td>
                    <td>{row.invoiceNumber || "—"} {row.approved && <span className="status-pill success">Approved</span>}</td>
                    <td>{row.invoiceDate || "—"}</td>
                    <td>{row.invoiceAmount == null ? "—" : `PKR ${format(row.invoiceAmount)}`}</td>
                    <td className={exceeds ? "negative-text" : ""}>
                      {row.difference == null ? "—" : `${row.difference > 0 ? "+" : ""}PKR ${format(row.difference)}`}
                    </td>
                    <td>
                      {row.difference == null
                        ? <span className="status-pill">Not generated</span>
                        : exceeds
                          ? <span className="status-pill danger">Exceeds PO</span>
                          : row.difference < 0
                            ? <span className="status-pill warning">Below PO</span>
                            : <span className="status-pill success">Matches PO</span>}
                    </td>
                  </tr>;
                })}
              </tbody>
            </table>
          </div>
        </section>

        {(payrollSegments.length > 0 || completedPayrollRows?.length > 0) && (
          <section className="panel" style={{ marginTop: 22 }}>
            <div className="panel-header">
              <div>
                <h2 className="panel-title">IIL Payroll Sheet</h2>
                <p className="page-copy" style={{ marginTop: 4 }}>
                  Every generated attendance batch is remembered in the month-to-date payroll draft. Advances recorded on the Advances tab are recovered automatically; run the combined payroll when all employees are included.
                </p>
              </div>
              <span className="badge">{payrollIsCompleted ? "Completed" : "Draft"}</span>
            </div>
            <div className="panel-body">
              <section className="card-grid" style={{ marginBottom: 0 }}>
                <div className="metric-card">
                  <div className="metric-label">Payroll Rows</div>
                  <div className="metric-value">{payrollSummary.employeeCount}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Salary and OT Charged to Client</div>
                  <div className="metric-value">PKR {format(payrollSummary.totalChargedToClient)}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Actual Wages Paid</div>
                  <div className="metric-value">PKR {format(payrollSummary.totalActualWagesPaid)}</div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Employee Deductions</div>
                  <div
                    className="metric-value"
                    style={{ color: hasPayrollDifference ? "#b45309" : "var(--success)" }}
                  >
                    PKR {format(payrollSummary.totalPayableDifference)}
                  </div>
                  <div className="page-copy" style={{ marginTop: 8 }}>
                    {hasPayrollDifference
                      ? `${payrollSummary.differenceRows} row(s): EOBI Rs. 400 per employee plus advances`
                      : "No employee deductions"}
                  </div>
                </div>
                <div className="metric-card">
                  <div className="metric-label">Payment Split</div>
                  <div className="metric-value" style={{ fontSize: 18 }}>
                    {payrollSummary.bankRows} bank / {payrollSummary.chequeRows} cheque
                  </div>
                </div>
              </section>

              {!payrollIsCompleted && approvedMonthKey && (advancePlan.byCode.size > 0 || advancePlan.notInPayroll.length > 0) && (
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ marginBottom: 6 }}>Recorded advances recovered in this payroll</h3>
                  <p className="page-copy" style={{ marginTop: 0 }}>Taken from the Advances tab. Change the amount to recover less (or more, up to what is owed); it is saved when you complete the payroll.</p>
                  <div className="table-wrap">
                    <table className="data-table">
                      <thead><tr><th>Employee</th><th>Owed</th><th>Recovering now</th><th>Balance after</th><th /></tr></thead>
                      <tbody>
                        {[...advancePlan.byCode.values()].map((row) => (
                          <tr key={row.code}>
                            <td>{row.code} — {row.name}</td>
                            <td>PKR {format(row.outstanding)}</td>
                            <td><input className="input" style={{ width: 130, padding: 8 }} type="number" min="0" value={advanceOverrides[row.code] ?? row.amount}
                              onChange={(event) => setAdvanceOverrides({ ...advanceOverrides, [row.code]: event.target.value })} /></td>
                            <td>PKR {format(row.balanceAfter)}</td>
                            <td>{advanceByCode.get(row.code) > 0 && <span className="status-pill warning">Also PKR {format(advanceByCode.get(row.code))} in the uploaded sheet — make sure it is not the same advance</span>}{row.capped && <span className="status-pill warning">Limited to salary after EOBI</span>}{row.leavesNegativeNet && <span className="status-pill danger">Net pay would be negative</span>}</td>
                          </tr>
                        ))}
                        {advancePlan.notInPayroll.map((entry) => (
                          <tr key={`np-${entry.code}`}><td>{entry.code} — {entry.name}</td><td>PKR {format(entry.outstanding)}</td><td colSpan={3}><span className="status-pill danger">Owes an advance but is not in this payroll</span></td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!approvedMonthKey && advanceLedger.advances.length > 0 && !payrollIsCompleted && (
                <p className="page-copy">Recorded advances are recovered once this payroll belongs to a monthly workspace (after the invoices are approved).</p>
              )}

              {payrollCompletedAt && (
                <p className="page-copy">Completed at {payrollCompletedAt}. This payroll snapshot will not change unless you reopen it.</p>
              )}

              {payrollNotice && <div className="management-alert error" role="alert" style={{ marginBottom: 12 }}>{payrollNotice}</div>}

              <div className="button-row">
                {payrollIsCompleted ? (
                  <button type="button" className="btn btn-muted" onClick={reopenPayrollDraft}>
                    Reopen Payroll
                  </button>
                ) : (
                  <button type="button" className="btn btn-success" onClick={markPayrollCompleted}>
                    Run and Complete Payroll
                  </button>
                )}
                <button type="button" className="btn btn-primary" onClick={downloadPayrollExcel}>
                  Download Payroll Excel
                </button>
              </div>
            </div>
          </section>
        )}

        {segments.length > 0 && (
          <section className="invoice-list">
            {segments.map((segment, index) => (
              <article className="invoice-card" key={segment.name}>
                <div className="invoice-card-header">
                  <div>
                    <h3 className="invoice-card-title">{segment.name}</h3>
                    <span className="badge">{segment.rows.length} employee(s)</span>
                  </div>
                  <div className="button-row" style={{ margin: 0, padding: 0, borderTop: 0 }}>
                    <div className="invoice-number-approval">
                      <input
                        className="input"
                        aria-label={`Invoice number for ${segment.name}`}
                        value={nextInvoiceNumber(index)}
                        disabled={Boolean(approvedInvoiceNumbers[segment.name])}
                        onChange={(event) => updateProposedInvoiceNumber(index, event.target.value)}
                      />
                      <button type="button" className="btn btn-success" disabled={loading} onClick={() => approveInvoiceNumber(segment, index)}>
                        {approvedInvoiceNumbers[segment.name] ? "Save Approved Changes" : "Approve & Save"}
                      </button>
                    </div>
                    <button type="button" className="btn btn-muted" disabled={!approvedInvoiceNumbers[segment.name]} onClick={() => downloadExcel(segment, index)}>
                      Download Excel
                    </button>
                    <button type="button" className="btn btn-primary" disabled={!approvedInvoiceNumbers[segment.name]} onClick={() => downloadOnePDF(segment, index)}>
                      Download PDF
                    </button>
                  </div>
                </div>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Invoice No</th>
                        <th>Salary Amount</th>
                        <th>PESSI</th>
                        <th>EOBI</th>
                        <th>Total Cost</th>
                        <th>Service</th>
                        <th>WHT</th>
                        <th>GST</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{nextInvoiceNumber(index)} {approvedInvoiceNumbers[segment.name] && <span className="status-pill success">Approved</span>}</td>
                        <td>{format(segment.summary.salaryAmount)}</td>
                        <td>{format(segment.summary.pessi)}</td>
                        <td>{format(segment.summary.eobi)}</td>
                        <td>{format(segment.summary.totalCost)}</td>
                        <td>{format(segment.summary.serviceCharges)}</td>
                        <td>{format(segment.summary.wht)}</td>
                        <td>{format(segment.summary.gst)}</td>
                        <td><strong>{format(segment.summary.totalInvoiceValue)}</strong></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {!approvedInvoiceNumbers[segment.name] && (
                  <TaxTreatmentPanel clientCode="IIL" normalized={{
                    serviceChargeSubtotal: segment.summary.serviceCharges,
                    gstAmount: segment.summary.gst,
                    grossAmount: segment.summary.totalInvoiceValue,
                    recoveryAddBack: segment.summary.wht
                  }} />
                )}
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
}

